// SKILL FUSIONS (owner 2026-11-02: 'a skill that is maxed out plus another skill that's maxed out should create a brand new
// type of powerful skill… combinations for almost all of them'). IP-NEUTRAL: original names, original mechanics.
//
// Rules (Docs/DESIGN/FUSIONS.md):
//  • Two rank-5 techniques (evolved or not) that form a recipe can FUSE. Both leave the build and become ONE fusion in a
//    single technique slot (a slot is freed). Fusions are a second, rarer tier on top of skill + trait evolutions.
//  • Offer: while both partners are at max rank, every level-up draft shows the gold FUSION card first (and chests can call
//    Sim.fusionOffer() / Sim.fuse()). Skipping it is free: it is offered again at the next draft.
//  • Resonance: an EVOLVED partner brings its evolution's strength into the fusion. The fusion's damage is multiplied by
//    1 + RES_K × (pair DPS as held / pair DPS unevolved − 1), from the measured per-skill table SKILL_DPS, so fusing two
//    evolved skills is never a downgrade (~1.6–1.8× the evolved pair it replaces).
//  • A partner that fused away can be taken again from rank 1 (it may feed a different recipe later); a fusion is owned once.
//  • Balance target: a fusion deals ~1.6–2.2× the summed rank-5 damage of its two partners (held-crowd bench,
//    tests/fusions.test.ts), since it also frees a slot. Hero damage never depends on the run pace; only travel speeds do.
//
// Pure + deterministic: no three, no DOM; only the Sim's seeded rng; every pick is sorted with the enemy id breaking ties.
import type { WeaponId } from './content';
import type { Enemy, Sim } from './sim';
import { inCone } from './burn';

export type FusionId = 'horizon' | 'sentinel' | 'tempest' | 'reaper' | 'eclipse' | 'shatter' | 'fault' | 'siren' | 'crushstar'
  | 'shackle' | 'monsoon' | 'scythelance' | 'moonstep' | 'stormhands' | 'legion' | 'phantom' | 'carom' | 'railcomet'
  | 'constellation' | 'prismnova' | 'frostpin' | 'lattice' | 'skybind' | 'roarfall' | 'cluster'
  | 'kiln' | 'solar' | 'magma' // fire recipes (live since the Fire Breath / Flame Ring merge)
  | 'prison' | 'hail'; // ICE FAMILY recipes (owner 2026-11-03): Glacier Prison, Hailstorm

/** Resonance damping: how much of the partners' evolution gain a fusion inherits */
export const RES_K = 0.85;
/** Held-crowd DPS of every fusable skill at rank 5 [unevolved, evolved] (tests/fusionBench.ts, base build, 20 s). Used only
 *  for Resonance; tests/fusions.test.ts re-measures it and fails when a skill's balance drifts by more than 20%. */
export const SKILL_DPS: Partial<Record<WeaponId, readonly [number, number]>> = {
  bolt: [448, 3588], orbit: [177, 401], nova: [184, 869], arc: [964, 3322], lance: [303, 1285], disc: [678, 1753], mortar: [261, 650],
  zenith: [693, 1026], breaker: [32, 56], riftline: [255, 331], skyfall: [261, 1229], mirage: [53, 140], glare: [41, 182], rush: [189, 833],
  ward: [0, 0], finger: [329, 897], barrage: [102, 315], phase: [121, 392], dive: [298, 862], shout: [45, 155], gaze: [286, 934],
  split: [419, 1026], crescent: [446, 751], snare: [125, 307], comet: [1042, 1724], rings: [388, 579], grip: [121, 323],
  breath: [222, 847], flamering: [285, 713],
  rime: [209, 850], spikes: [344, 949], glacier: [390, 771], blizzard: [315, 945], // ICE FAMILY (owner 2026-11-03; tests/iceFamily.test.ts re-measures)
};
/** Held-crowd DPS of every fusion made from UNEVOLVED partners (same bench, Resonance 1). Read by the evolution floor;
 *  tests/fusions.test.ts re-measures it and fails beyond ±20% drift (re-record it after a balance pass). */
export const FUSION_DPS: Record<FusionId, number> = {
  horizon: 638, sentinel: 1185, tempest: 2178, reaper: 3138, eclipse: 2122, shatter: 427, fault: 1049, siren: 187, crushstar: 1551,
  shackle: 2980, monsoon: 804, scythelance: 1121, moonstep: 1074, stormhands: 726, legion: 983, phantom: 896, carom: 2466, railcomet: 2612,
  constellation: 1558, prismnova: 349, frostpin: 706, lattice: 304, skybind: 1074, roarfall: 784, cluster: 1489, kiln: 966, solar: 885, magma: 1063,
  prison: 856, hail: 865, // ICE FAMILY (owner 2026-11-03)
};
/** EVOLUTION FLOOR (owner 2026-11-03: 'fusions should be better than evolutions'): whatever its partners were, a fusion deals
 *  at least EVO_FLOOR × the held-crowd DPS of the stronger of its two parents' EVOLVED forms. Resonance is raised to that
 *  floor when the formula below falls short (unevolved partners, or only the weaker one evolved). */
export const EVO_FLOOR = 1.15;
/** the Resonance a fusion needs to sit on the evolution floor (1 when its unevolved form already clears it) */
export function evoFloor(id: FusionId): number {
  const f = FUSIONS[id], a = SKILL_DPS[f.a] ?? [0, 0], b = SKILL_DPS[f.b] ?? [0, 0], own = FUSION_DPS[id];
  return own > 0 ? Math.max(1, EVO_FLOOR * Math.max(a[1], b[1]) / own) : 1;
}
/** evolved-partner mask: bit 1 = partner a evolved, bit 2 = partner b evolved */
export const evoMask = (evoA: boolean, evoB: boolean): number => (evoA ? 1 : 0) | (evoB ? 2 : 0);
/** Resonance damage multiplier of a fusion made from partners with this evolved mask (never below the evolution floor) */
export function resonance(id: FusionId, mask: number): number {
  const f = FUSIONS[id], a = SKILL_DPS[f.a] ?? [0, 0], b = SKILL_DPS[f.b] ?? [0, 0], base = a[0] + b[0], floor = evoFloor(id);
  if (!mask || base <= 0) return floor;
  const held = (mask & 1 ? a[1] : a[0]) + (mask & 2 ? b[1] : b[0]);
  return Math.max(floor, 1 + RES_K * Math.max(0, held / base - 1));
}
/** Shared accent colour of everything fused (owner 2026-11-02: an epic emerald; render: the rim, the fusion moment, fused
 *  beam / link cores; HUD: the emerald FUSION card). */
export const FUSION_ACCENT = 0x3dffa8;

/** Stat line shown on cards / the pause planner: keys into the fusion's tuning `n` (so a card can't disagree with the sim). */
export interface FusionCard { dmg: string; dmgLabel: string; area?: string; areaLabel?: string; count?: string | number; countLabel?: string;
  /** one more line from the tuning; d = the live damage multiplier */ extra?: (n: Record<string, number>, d: number) => string }
const r0 = (v: number): string => String(Math.round(v));
export interface FusionDef {
  id: FusionId; a: WeaponId; b: WeaponId; name: string;
  /** one-line fantasy */ fantasy: string;
  /** what it does (cards, Encyclopedia) */ desc: string;
  /** icon / VFX brief for the art partner */ art: string;
  /** main colour and second colour (render; the gold accent comes on top) */ col: number; col2: number;
  card: FusionCard;
  /** tuning (read by the sim behaviour of this fusion) */ n: Record<string, number>;
}

const def = (d: FusionDef): FusionDef => d;
/** The recipe table (30). Order = Encyclopedia order; the draft offers the first ready recipe in this order. */
export const FUSIONS: Record<FusionId, FusionDef> = {
  horizon: def({ id: 'horizon', a: 'lance', b: 'breaker', name: 'Event Horizon', col: 0xffe28a, col2: 0xff1a3a,
    fantasy: 'A beam with a black hole riding down it.',
    desc: 'Charges a wide lance beam at the biggest group. A singularity rides down the beam from your hand to its tip, dragging every foe nearby into the light, then implodes at the tip.',
    art: 'Icon: a gold beam piercing a black orb ringed in red lightning. VFX: the orb sliding down the beam while foes slide sideways into the light.',
    card: { dmg: 'dmg', dmgLabel: 'DMG/TICK', area: 'w', areaLabel: 'WIDTH', extra: (n, d) => `Singularity ${r0(n.well * d)} / 0.25 s · implosion ${r0(n.implode * d)}` },
    n: { cd: 6.5, charge: 0.45, dur: 2.0, len: 26, w: 2.4, dmg: 19, well: 29, wellR: 2.4, pull: 7.5, pullR: 6, wellFrom: 5, implode: 212, implodeR: 6, bite: 0.03 } }),
  sentinel: def({ id: 'sentinel', a: 'orbit', b: 'bolt', name: 'Sentinel Halo', col: 0xffd23a, col2: 0xfff4c0,
    fantasy: 'Orbiting spheres that shoot back.',
    desc: 'Six spheres orbit you and burn what they touch. Each one is a turret: when it sweeps past a foe it spits a homing bolt at it.',
    art: 'Icon: a ring of orbs with tiny comet tails flying outward. VFX: orbs flicking bright yellow bolts as they swing past the crowd.',
    card: { dmg: 'bolt', dmgLabel: 'BOLT', area: 'radius', areaLabel: 'ORBIT', count: 'orbs', countLabel: 'SPHERES', extra: (n, d) => `Contact ${r0(n.touch * d)}` },
    n: { orbs: 6, radius: 4.8, spin: 2.6, orbR: 0.85, touch: 44, rehit: 0.4, gap: 0.5, reach: 9, bolt: 54, bite: 0.11 } }),
  tempest: def({ id: 'tempest', a: 'orbit', b: 'arc', name: 'Tempest Crown', col: 0x8fd8ff, col2: 0xffffff,
    fantasy: 'A spinning crown of linked lightning.',
    desc: 'Five spheres orbit you, linked by standing lightning into a spinning electric ring that shocks every foe crossing a link. Every second each sphere also lashes out at a foe nearby.',
    art: 'Icon: a pentagon of blue-white orbs joined by jagged lightning. VFX: a crackling rotating crown around the hero.',
    card: { dmg: 'link', dmgLabel: 'LINK', area: 'radius', areaLabel: 'RING', count: 'orbs', countLabel: 'SPHERES', extra: (n, d) => `Lash ${r0(n.zap * d)} × ${n.jumps} jumps` },
    n: { orbs: 5, radius: 5.4, spin: 1.5, link: 34, linkW: 0.75, linkGap: 10, lash: 1.0, jumps: 4, zap: 55, first: 7, jump: 5.5, bite: 0.068 } }),
  reaper: def({ id: 'reaper', a: 'disc', b: 'arc', name: 'Thunder Reaper', col: 0xc6b8ff, col2: 0x9fd8ff,
    fantasy: 'Boomerang blades that throw lightning.',
    desc: 'Throws four crackling discs that loop out and back. In flight each disc whips chain lightning into the foes beside its path several times a second.',
    art: 'Icon: a spinning disc throwing forked lightning. VFX: discs looping out while lightning lashes from them in every direction.',
    card: { dmg: 'dmg', dmgLabel: 'DMG/PASS', area: 'range', areaLabel: 'RANGE', count: 'count', countLabel: 'DISCS', extra: (n, d) => `Lightning ${r0(n.zap * d)} × ${n.jumps} jumps` },
    n: { cd: 1.5, count: 4, range: 14, r: 1.6, speed: 24, dmg: 63, zapGap: 12, jumps: 3, zap: 46, first: 5, jump: 5, bite: 0.02 } }),
  eclipse: def({ id: 'eclipse', a: 'disc', b: 'crescent', name: 'Eclipse Wheel', col: 0xb8a6ff, col2: 0x2a1840,
    fantasy: 'Crescent wheels that come home as a halo.',
    desc: 'Throws three great crescent wheels that loop out like boomerangs. When they come back they circle you twice as a cutting halo before fading.',
    art: 'Icon: two crescents interlocked into a dark-and-gold ring. VFX: the wheels settling into a whirling ring of blades around the hero.',
    card: { dmg: 'dmg', dmgLabel: 'DMG/CUT', area: 'range', areaLabel: 'RANGE', count: 'count', countLabel: 'WHEELS', extra: (n) => `Halo ${n.haloT} s` },
    n: { cd: 2.4, count: 3, range: 15, r: 1.9, speed: 24, dmg: 56, haloR: 3.6, haloSpin: 4.2, haloT: 3.0, rehit: 0.35, haloPush: 9, bite: 0.04 } }),
  shatter: def({ id: 'shatter', a: 'nova', b: 'glare', name: 'Shatterpulse', col: 0xe8f8ff, col2: 0x7fe8ff,
    fantasy: 'Freeze the crowd, then break it.',
    desc: 'A white pulse freezes every foe around you in crystal light. A moment later they all shatter in a second shockwave that hits harder the more foes were frozen.',
    art: 'Icon: a cracked white sphere with shards bursting out. VFX: frozen silhouettes, then a prismatic shatter ring.',
    card: { dmg: 'shatter', dmgLabel: 'SHATTER', area: 'r', areaLabel: 'RADIUS', extra: (n, d) => `Freeze ${n.freeze} s (hit ${r0(n.dmg * d)}) · +${Math.round(n.per * 100)}% per frozen foe` },
    n: { cd: 3.0, r: 7.5, dmg: 21, freeze: 1.2, delay: 0.6, shatter: 64, per: 0.04, perMax: 15, bite: 0.18 } }),
  fault: def({ id: 'fault', a: 'dive', b: 'riftline', name: 'Fault Drop', col: 0xff8a3a, col2: 0xff80ff,
    fantasy: 'Land so hard the ground splits four ways.',
    desc: 'Leap and crash down on the crowd. The impact splits the ground into four rolling walls of jagged energy that race outward in a cross from the crater.',
    art: 'Icon: a crater with four glowing fissures in a cross. VFX: the landing flash, then four walls erupting outward.',
    card: { dmg: 'wall', dmgLabel: 'WALL', area: 'ww', areaLabel: 'WALL WIDTH', count: 'walls', countLabel: 'WALLS', extra: (n, d) => `Landing ${r0(n.land * d)}` },
    n: { cd: 4.5, air: 0.55, reach: 8, r: 5.2, land: 140, launch: 9, landKnock: 1.6, landSafe: 0.6, walls: 4, wlen: 13, ww: 6.5, wspeed: 16, wall: 107, push: 18, bite: 0.11 } }),
  siren: def({ id: 'siren', a: 'mirage', b: 'shout', name: 'Siren Decoy', col: 0xd8c0ff, col2: 0xd2ecff,
    fantasy: 'A decoy that gathers them, then roars.',
    desc: 'Leaves a shimmering decoy that draws foes to it. Then it roars in every direction, hurling and stunning the packed crowd, and bursts.',
    art: 'Icon: a ghostly figure with concentric sound rings. VFX: foes clumping round the decoy, then a ring of rippling force throwing them out.',
    card: { dmg: 'roar', dmgLabel: 'ROAR', area: 'roarR', areaLabel: 'ROAR R', extra: (n, d) => `Stun ${n.stun} s · burst ${r0(n.burst * d)}` },
    n: { cd: 3.6, life: 2.2, pullR: 9, pull: 5, aura: 3.6, auraR: 2.5, roarR: 8, roar: 32, hurl: 22, stun: 1.2, burst: 24, burstR: 3.5, bite: 0.07 } }),
  crushstar: def({ id: 'crushstar', a: 'grip', b: 'zenith', name: 'Crushing Star', col: 0xffe9a8, col2: 0xff6ad0,
    fantasy: 'Crush the crowd into a star and throw it.',
    desc: 'Seizes up to 20 foes, lifts them and crushes them into a burning star overhead, hurting them as it compresses. Then it hurls the star at the densest crowd: the blast grows with every foe crushed.',
    art: 'Icon: a hand clenching a small star. VFX: foes spiralling up into a forming orb, then a hurled meteor blast.',
    card: { dmg: 'blast', dmgLabel: 'BLAST', area: 'br', areaLabel: 'BLAST', count: 'max', countLabel: 'SEIZED', extra: (n, d) => `+${r0(n.perFoe * d)} per foe crushed · crush ${r0(n.crush * d)}` },
    n: { cd: 5.5, reach: 15, catchR: 7, max: 20, lift: 0.9, hold: 0.3, h: 5, tick: 26, crush: 112, fly: 0.55, blast: 388, perFoe: 26, br: 5.5, brK: 0.2, brMax: 9, bite: 0.18 } }),
  shackle: def({ id: 'shackle', a: 'rings', b: 'arc', name: 'Conductive Shackles', col: 0xc08aff, col2: 0x9fd8ff,
    fantasy: 'Pin them, then run a current through all of them.',
    desc: 'Rings pin up to 14 foes in place and lightning runs in a loop from pinned foe to pinned foe, shocking anyone caught on the current. Then the rings shatter for a burst.',
    art: 'Icon: three rings strung on a lightning thread. VFX: violet rings on foes with a crackling circuit hopping between them.',
    card: { dmg: 'zap', dmgLabel: 'CURRENT', area: 'catchR', areaLabel: 'CATCH', count: 'max', countLabel: 'RINGS', extra: (n, d) => `Shatter ${r0(n.burst * d)} · hold ${n.hold} s` },
    n: { cd: 3.6, reach: 14, catchR: 8, max: 14, hold: 2.4, gap: 12, zap: 50, wireW: 0.7, burst: 109, burstR: 2.4, bite: 0.035 } }),
  monsoon: def({ id: 'monsoon', a: 'barrage', b: 'skyfall', name: 'Flare Monsoon', col: 0xffb02a, col2: 0xa8d8ff,
    fantasy: 'A walking carpet of falling fire.',
    desc: 'Both hands pour flares into the sky. They rain down as a walking carpet of mini fireballs that sweeps across the thickest crowd.',
    art: 'Icon: small flame drops falling from a cloud of sparks. VFX: a moving band of orange pops marching through the horde.',
    card: { dmg: 'dmg', dmgLabel: 'DMG/DROP', area: 'br', areaLabel: 'BURST', extra: (n) => `${Math.round(n.dur * 60 / n.every)} drops · walks ${n.walk} m/s` },
    n: { cd: 3.0, reach: 18, dur: 2.6, every: 2, spread: 3.4, walk: 3.5, fall: 0.32, dmg: 20, br: 1.7, bite: 0.024 } }),
  scythelance: def({ id: 'scythelance', a: 'lance', b: 'gaze', name: 'Scythe Lance', col: 0xfff2c0, col2: 0xff5a1a,
    fantasy: 'A beam swung like a scythe.',
    desc: 'The lance sweeps through a wide arc in front of you instead of firing straight, and the ground it crosses stays ablaze for a few seconds, burning every foe that walks through.',
    art: 'Icon: a beam drawn as a curved scythe blade with a fire line under it. VFX: a fan-shaped sweep leaving a burning arc on the ground.',
    card: { dmg: 'dmg', dmgLabel: 'DMG/TICK', area: 'w', areaLabel: 'WIDTH', extra: (n, d) => `${n.arc}° sweep · burn ${r0(n.burn * d)} for ${n.burnT} s` },
    n: { cd: 4.0, charge: 0.3, dur: 1.3, len: 22, w: 1.8, arc: 140, dmg: 39, gap: 5, burnT: 3, burn: 16, burnW: 0.9, bite: 0.043 } }),
  moonstep: def({ id: 'moonstep', a: 'phase', b: 'crescent', name: 'Moonstep', col: 0xc3a6ff, col2: 0xb8a6ff,
    fantasy: 'Every blink throws a crescent.',
    desc: 'Blink from foe to foe, slashing each one. At every arrival a crescent is hurled onward along the slash, cutting through the crowd behind.',
    art: 'Icon: a fading afterimage with a crescent leaving it. VFX: violet blink trail with crescents peeling off every stop.',
    card: { dmg: 'cdmg', dmgLabel: 'CRESCENT', area: 'cw', areaLabel: 'WIDTH', count: 'blinks', countLabel: 'BLINKS', extra: (n, d) => `Slash ${r0(n.slash * d)}` },
    n: { cd: 2.6, blinks: 7, first: 10, jump: 7.5, gap: 5, slash: 82, slashL: 3.5, slashW: 0.8, crange: 9, cspeed: 22, cw: 4, cdmg: 90, thick: 0.7, bite: 0.051 } }),
  stormhands: def({ id: 'stormhands', a: 'phase', b: 'rush', name: 'Storm of Hands', col: 0xff8f8f, col2: 0xc3a6ff,
    fantasy: 'Be everywhere the crowd is, all at once.',
    desc: 'Blink to the thickest clusters one after another and unleash a full-circle flurry of strikes at each, then snap back.',
    art: 'Icon: many afterimage fists radiating from one point. VFX: rapid strike flashes ringing each cluster.',
    card: { dmg: 'dmg', dmgLabel: 'DMG/STRIKE', area: 'r', areaLabel: 'FLURRY R', count: 'stops', countLabel: 'STOPS', extra: (n) => `${n.strikes} strikes per stop` },
    n: { cd: 3.0, stops: 4, reach: 14, gap: 16, strikes: 5, sgap: 3, r: 3.4, dmg: 18, knock: 0.8, stagger: 0.4, bite: 0.028 } }),
  legion: def({ id: 'legion', a: 'split', b: 'barrage', name: 'Gatling Legion', col: 0xffb02a, col2: 0x6ff0e0,
    fantasy: 'Copies of you with flare cannons.',
    desc: 'Summons three glowing copies at your side for 6 s. Each pours a stream of flares into the nearest foes, every flare bursting in a mini fireball.',
    art: 'Icon: three silhouettes with streams of sparks from their hands. VFX: copies at your side firing orange streams.',
    card: { dmg: 'dmg', dmgLabel: 'DMG/FLARE', area: 'br', areaLabel: 'BURST', count: 'copies', countLabel: 'COPIES', extra: (n) => `${n.dur} s · ${Math.round(60 / n.gap)} flares/s each` },
    n: { cd: 7.5, copies: 3, dur: 6, ring: 2.6, spin: 0.8, gap: 4, reach: 15, speed: 34, range: 15, dmg: 24, br: 1.4, bite: 0.013 } }),
  phantom: def({ id: 'phantom', a: 'split', b: 'mirage', name: 'Phantom Host', col: 0xd8c0ff, col2: 0x6ff0e0,
    fantasy: 'Decoys that fight back, then explode.',
    desc: 'Three copies appear around you and draw foes to themselves, firing bolts while they do. When a copy fades it detonates.',
    art: 'Icon: a translucent figure surrounded by inward arrows. VFX: crowds bunching around shimmering copies, then each copy pops.',
    card: { dmg: 'bolt', dmgLabel: 'BOLT', area: 'br', areaLabel: 'BLAST', count: 'copies', countLabel: 'COPIES', extra: (n, d) => `Blast ${r0(n.blast * d)} · ${n.dur} s` },
    n: { cd: 6.0, copies: 3, dist: 5.5, dur: 4, pullR: 8, pull: 4.5, gap: 0.3, reach: 10, bolt: 30, blast: 274, br: 4.5, bite: 0.16 } }),
  carom: def({ id: 'carom', a: 'mortar', b: 'comet', name: 'Caroming Sun', col: 0x9aff5a, col2: 0x5affd8,
    fantasy: 'A bomb that will not stop bouncing.',
    desc: 'Hurls a heavy blazing sphere that bounces from foe to foe, exploding on every bounce. Each blast is bigger than the last.',
    art: 'Icon: a fireball with a zig-zag trail of impact stars. VFX: green-gold sphere ricocheting through the crowd with chained blasts.',
    card: { dmg: 'dmg', dmgLabel: 'BLAST', area: 'br', areaLabel: 'BLAST', count: 'bounces', countLabel: 'BOUNCES', extra: (n) => `+${Math.round(n.grow * 100)}% size, +${Math.round(n.dgrow * 100)}% dmg per bounce` },
    n: { cd: 2.0, reach: 20, speed: 22, r: 0.75, bounces: 8, seek: 8, dmg: 86, br: 2.6, grow: 0.12, dgrow: 0.1, life: 4, bite: 0.039 } }),
  railcomet: def({ id: 'railcomet', a: 'comet', b: 'finger', name: 'Railshot Comet', col: 0x5affd8, col2: 0xff1a3c,
    fantasy: 'Every ricochet fires a needle ahead.',
    desc: 'Hurls a comet that ricochets through the crowd. At every bounce it fires a crimson needle ahead to its next target that pierces every foe on the line.',
    art: 'Icon: a comet with a straight red line shooting ahead of it. VFX: teal comet zig-zagging, crimson needles cracking out at each turn.',
    card: { dmg: 'needle', dmgLabel: 'NEEDLE', area: 'nlen', areaLabel: 'NEEDLE', count: 'hits', countLabel: 'HITS', extra: (n, d) => `Comet hit ${r0(n.hit * d)}` },
    n: { cd: 1.7, reach: 18, speed: 26, r: 0.6, hits: 10, seek: 9, hit: 117, needle: 100, nlen: 18, nw: 0.45, life: 4, bite: 0.035 } }),
  constellation: def({ id: 'constellation', a: 'snare', b: 'zenith', name: 'Constellation Heart', col: 0xffe08c, col2: 0xa8f6ff,
    fantasy: 'Stars that fuse into one falling heart.',
    desc: 'A ring of star-orbs gathers around the crowd and holds it in. Instead of slamming inward, they fuse into one giant orb that rises and drops on the centre.',
    art: 'Icon: small stars converging into one large star. VFX: a ring of lights drawing together overhead, then a heavy drop.',
    card: { dmg: 'dmg', dmgLabel: 'DROP', area: 'br', areaLabel: 'BLAST', count: 'orbs', countLabel: 'STARS', extra: (n) => `Holds foes in for ${n.hang} s` },
    n: { cd: 3.8, reach: 16, ring: 6.2, orbs: 10, hang: 1.1, rise: 0.5, hold: 0.25, drop: 0.25, h: 7, pull: 5, dmg: 623, br: 7.5, bite: 0.3 } }),
  prismnova: def({ id: 'prismnova', a: 'ward', b: 'nova', name: 'Prism Nova', col: 0xbfeaff, col2: 0xffc8f0,
    fantasy: 'A crystal dome that keeps ringing.',
    desc: 'A crystal dome forms around you and absorbs 3 hits. While it stands it pulses refracted shock rings around you, and every hit it absorbs triggers a far bigger pulse.',
    art: 'Icon: a crystal dome with rainbow rings rippling off. VFX: prismatic rings expanding from a faceted dome.',
    card: { dmg: 'dmg', dmgLabel: 'PULSE', area: 'r', areaLabel: 'RADIUS', count: 'charges', countLabel: 'HITS HELD', extra: (n, d) => `Absorb pulse ${r0(n.big * d)} · re-forms after ${n.recharge} s` },
    n: { gap: 1.2, r: 5.6, dmg: 75, charges: 3, recharge: 6, big: 206, bigR: 8.5, bite: 0.3 } }),
  frostpin: def({ id: 'frostpin', a: 'glare', b: 'finger', name: 'Stasis Needle', col: 0xffffff, col2: 0xff1a3c,
    fantasy: 'Needles that freeze, then shatter what they froze.',
    desc: 'Fires a fan of three needles that freeze every foe they pierce. A needle that pierces an already frozen foe shatters it, splashing the foes around it.',
    art: 'Icon: a thin needle passing through an ice-white diamond. VFX: white-crimson needle lines with crystal pops.',
    card: { dmg: 'dmg', dmgLabel: 'NEEDLE', area: 'range', areaLabel: 'RANGE', count: 'needles', countLabel: 'NEEDLES', extra: (n, d) => `Shatter ${r0(n.shatter * d)} in ${n.sr} m · freeze ${n.freeze} s` },
    n: { cd: 0.9, range: 30, w: 0.45, fan: 0.12, needles: 3, dmg: 28, freeze: 1.0, shatter: 23, sr: 2.0, bite: 0.0136 } }),
  lattice: def({ id: 'lattice', a: 'snare', b: 'breaker', name: 'Gravity Lattice', col: 0x1a0a14, col2: 0xff2a3a,
    fantasy: 'A ring of black holes that becomes one.',
    desc: 'Six small black holes hang in a ring around the crowd, each dragging foes toward it and hurting what it holds. Then they all fall to the centre in one implosion.',
    art: 'Icon: a ring of tiny black orbs around a red core. VFX: foes tugged between the holes, then one crimson implosion.',
    card: { dmg: 'implode', dmgLabel: 'IMPLOSION', area: 'ring', areaLabel: 'RING', count: 'holes', countLabel: 'HOLES', extra: (n, d) => `Holes ${r0(n.tick * d)} / 0.25 s` },
    n: { cd: 3.6, reach: 16, holes: 6, ring: 6, spin: 0.7, life: 2.0, pullR: 4.5, pull: 6, coreR: 1.6, tick: 10, merge: 0.3, implode: 115, ir: 6.5, bite: 0.11 } }),
  skybind: def({ id: 'skybind', a: 'rings', b: 'grip', name: 'Skybind Press', col: 0xff6ad0, col2: 0xc08aff,
    fantasy: 'Bind them in the sky, then drive them down.',
    desc: 'Yanks up to 14 foes into the sky and clamps them in a tight spinning knot of binding rings that crush them as they tighten. Then the whole knot is driven into the ground: a brutal slam that finishes off any bound foe left weak, and a shockwave that hurls and dazes everything around the crater. More bound foes make a bigger shock; area upgrades bind more.',
    art: 'Icon: a knot of figures clamped by glowing rings above a crater. VFX: foes yanked skyward on pink streaks into a knot of violet clamp rings that tighten and glow, then a white press column, a crater and a pink shock ring.',
    card: { dmg: 'slam', dmgLabel: 'SLAM', area: 'catchR', areaLabel: 'CATCH', count: 'max', countLabel: 'BOUND', extra: (n, d) => `Shock ${r0(n.shock * d)} +${r0(n.perFoe * d)} per bound foe · finishes foes under ${r0(n.exec * 100)}% HP` },
    // lift: the yank up; bound: the knot (rings tick every `gap` ticks); drop: the press down; slam / exec: per bound foe;
    // shock + perFoe × bound: the ground shock (knock + stun) in shockR; the bound cap and the catch radius scale with area
    n: { cd: 4.2, reach: 15, catchR: 6, max: 14, lift: 0.3, bound: 0.85, drop: 0.12, h: 4.4, spin: 5, inner: 1.5, gap: 10, tick: 15, slam: 205, exec: 0.15, shock: 55, perFoe: 5, shockR: 7, stun: 0.7, bite: 0.08 } }),
  // owner 2026-11-03 ('Rework Roaring Meteor, very underwhelming, need to feel more Earth shattering power, impact and ground
  // deformation/crater'): the leap is longer and higher (a hang at the apex over a growing landing mark), the landing is
  // the big hit (a deep crater, hard launch + daze), then the three roars hurl, flip and stun. The id stays `roarfall`.
  roarfall: def({ id: 'roarfall', a: 'shout', b: 'dive', name: 'Roaring Meteor', col: 0xff8a3a, col2: 0xd2ecff,
    fantasy: 'Fall like a star. Land like a war cry.',
    desc: 'Leap high over the crowd, hang for a breath while the landing mark grows beneath them, then come down as a meteor. The impact blasts a deep crater: everything in it takes the big hit, is hurled into the air and dazed. Then three roaring shockwaves tear out across the ground, each hurling and flipping foes and stunning them, every echo a little softer. Props in the crater shatter and the ground stays cracked and glowing for a while.',
    art: 'Icon: a falling star with sound rings at its base. VFX: a white streak up, an orange charging glow at the apex over a growing target ring, a fiery meteor trail down, a white flash and a dust and rock eruption, a deep crater with a raised rim and radial molten cracks that cool, three heat-haze roar rings with dust walls.',
    card: { dmg: 'land', dmgLabel: 'IMPACT', area: 'roarR', areaLabel: 'ROAR R', count: 3, countLabel: 'ROARS', extra: (n, d) => `Crater ${r0(n.r)} m · roar ${r0(n.roar * d)} (echoes ${r0(n.echo * 100)}%) · hurl + stun ${n.stun} s` },
    // air: airtime (the sim's path; render adds the height); r: crater (landing) radius; land: landing hit; landKnock / launch:
    // the landing's shove and lift; landStun: daze in the crater; landSafe: cover after the landing; crater: sim crater share of r;
    // roarR: the roar reach; roar: ring damage (echo × for rings 2 and 3, echoGap s apart); hurl / lift: the rings' throw; stun: ring stun
    n: { cd: 4.2, air: 0.78, reach: 8, r: 5.5, land: 180, landKnock: 1.4, launch: 10, landStun: 0.5, landSafe: 0.6, crater: 0.8, landBite: 1.35, roarR: 9.5, roar: 30, hurl: 25, lift: 5.5, echoHurl: 0.45, echoLiftK: 0.3, stun: 1.0, echo: 0.7, echoGap: 0.42, bite: 0.14 } }),
  // owner 2026-11-03 ('Don't like cluster volley fusion, underwhelming'): redesigned as a surround-and-converge barrage. The
  // id stays `cluster` (save / Encyclopedia / icon keys); the name, mechanics, look and sound are new.
  cluster: def({ id: 'cluster', a: 'bolt', b: 'mortar', name: 'Converging Volley', col: 0x46e05a, col2: 0xffd23a,
    fantasy: 'Miss on purpose. Then close the ring.',
    desc: 'Fires a rapid spread of twelve bolt-orbs that swing wide and hang in a pulsing ring around the thickest crowd while a heavy shell lobs onto its centre. After a beat every orb dives inward at once, each bursting on the first foe it meets; foes inside the ring are dragged to the centre, where the shell detonates, harder for every foe trapped. The blast stuns the crowd and finishes ordinary foes left below 15% health.',
    art: 'Icon: a ring of small yellow suns around a falling green shell. VFX: orbs scatter and hover in a pulsing ring (brighter just before they dive), then all streak inward into one green-gold detonation with a column of light and a chain of small bursts.',
    card: { dmg: 'dmg', dmgLabel: 'CORE BLAST', area: 'ring', areaLabel: 'RING', count: 'orbs', countLabel: 'ORBS', extra: (n, d) => `Orb burst ${r0(n.orb * d)} in ${n.orbR} m · core +${r0(n.perFoe * 100)}% per trapped foe (max +${r0(n.maxBonus * 100)}%) · stun ${n.stun} s · finishes foes under ${r0(n.exec * 100)}%` },
    n: { cd: 2.4, reach: 20, ring: 5.2, orbs: 12, gap: 2, fly: 0.5, hover: 0.45, dive: 0.2, h: 6.5, dmg: 245, br: 4.6, orb: 62, orbR: 1.9, perFoe: 0.06, maxBonus: 1.2, stun: 0.8, pull: 14, exec: 0.15, execElite: 0.06, lead: 0.5, bite: 0.03 } }),
  // ---- fire recipes (reserved until the Fire Breath / Flame Ring merge; every burn is the shared burn status, src/core/burn.ts) ----
  kiln: def({ id: 'kiln', a: 'breath', b: 'gaze', name: 'Kiln Sight', col: 0xff8a2a, col2: 0xfff0b0,
    fantasy: 'Eye beams that sweep through your own fire.',
    desc: 'Breathe a cone of flame while two eye beams scissor back and forth through it. Everything in the flame catches fire, and every strip of ground the beams cross becomes a burning field for a few seconds.',
    art: 'Icon: two crossing amber eye beams inside a flame cone over glowing ground. VFX: a fire cone with two white-gold beams sweeping through it, leaving fanned strips of burning ground.',
    card: { dmg: 'beam', dmgLabel: 'BEAM', area: 'cone', areaLabel: 'CONE', count: 2, countLabel: 'BEAMS', extra: (n, d) => `Flame ${r0(n.flame * d)} + burn ${r0(n.burn * d)}/s · burning ground ${r0(n.field * d)}/s for ${n.fieldT} s` },
    n: { cd: 4.0, dur: 1.8, cone: 10, half: 0.45, tick: 6, flame: 11.5, burn: 25, burnT: 3, len: 12.5, bw: 0.6, gap: 5, beam: 11.5, passes: 2, drop: 4, field: 25, fieldT: 3, fw: 0.6, after: 0.5, bite: 0.014 } }),
  solar: def({ id: 'solar', a: 'flamering', b: 'orbit', name: 'Solar Orrery', col: 0xffa030, col2: 0xff5a14,
    fantasy: 'Little suns that set the ground alight.',
    desc: 'Four small suns orbit you, scorching and igniting what they touch, and leave a ring of burning ground behind them. Every few seconds each sun flares in a burst of flame.',
    art: 'Icon: four small suns on an orbit around a burning ring. VFX: orange suns circling the hero over a ring of fire, with flame rings bursting from each one.',
    card: { dmg: 'touch', dmgLabel: 'CONTACT', area: 'radius', areaLabel: 'ORBIT', count: 'orbs', countLabel: 'SUNS', extra: (n, d) => `Flare ${r0(n.flareDmg * d)} every ${n.flare} s · burning trail ${r0(n.trail * d)}/s` },
    n: { orbs: 4, radius: 4.4, spin: 2.0, orbR: 1.0, touch: 74, rehit: 0.5, burn: 37, burnT: 3, trailGap: 6, trail: 37, trailT: 1.6, tw: 0.6, flare: 3.0, flareR: 2.8, flareDmg: 146, after: 0.5, bite: 0.09 } }),
  // Magma Front (owner 2026-11-03: 'too underwhelming, bad animations' → rework): the ground splits along a fissure through
  // the thickest line of foes and drags the lane onto it (fissure s), magma erupts into a wall that rolls wlen m, flinging
  // what it catches up and back (push / loft) and burning it; the stripe behind it is lava (shared burn `molten`/s refreshed
  // while on it, plus `sear` direct damage every 0.25 s that STACKS with the burn, for moltenT s); it collapses in a splash
  // (splashR / splash, a molten pool + `blobs` lava spots); any foe that dies burning with this fire pops into a small lava
  // splash (popR / pop, at most popMax a tick). Demolition does not touch it (no detonation in it).
  magma: def({ id: 'magma', a: 'breath', b: 'riftline', name: 'Magma Front', col: 0xff5a14, col2: 0xffc040,
    fantasy: 'The ground splits, and a wall of magma rolls out of the crack.',
    desc: 'Splits the ground along a glowing fissure through the thickest line of foes and drags them onto it. Magma erupts from the crack into a tall wall of fire that rolls down the line, flinging everything it catches up and back and setting it burning, and leaves a stripe of molten lava that keeps searing anyone standing on it. Where the wall ends it collapses in a magma splash, and foes that die burning erupt into small lava splashes of their own.',
    art: 'Icon: a tall curling wave of flame rising out of a glowing lava crack. VFX: the fissure cracking open ahead of the hero, a curling wall of fire with a molten base rolling down it, a cooling yellow-to-crust lava stripe behind, a magma splash with lava blobs at the end.',
    card: { dmg: 'wall', dmgLabel: 'WALL', area: 'ww', areaLabel: 'WALL WIDTH', extra: (n, d) => `Burn ${r0(n.burn * d)}/s · lava ${r0(n.sear * 4 * d)}/s + burn for ${n.moltenT} s · splash ${r0(n.splash * d)} · lava pops ${r0(n.pop * d)}` },
    n: { cd: 2.8, fissure: 0.3, ahead: 1.6, ww: 8.5, wlen: 17, wspeed: 13, pullW: 3.5, pull: 14, wall: 100, feed: 0.06, feedMax: 1.0, push: 14, loft: 7, burn: 20, burnT: 3, drop: 0.9, mw: 0.55, molten: 20, sear: 2, moltenT: 4, after: 0.5, splashR: 4.5, splash: 100, blobs: 4, popR: 3, pop: 120, popMax: 8, bite: 0.071 } }),
  // ---- ICE FAMILY recipes (owner 2026-11-03): every slow is the shared chill (Sim.chill), every freeze Sim.iceFreeze ----
  prison: def({ id: 'prison', a: 'grip', b: 'spikes', name: 'Glacier Prison', col: 0xbdf0ff, col2: 0xff6ad0,
    fantasy: 'Lift the crowd, lock it in ice, drop the block.',
    desc: 'Seizes up to 16 foes, lifts them and encases the whole cluster in a block of ice: frozen helpless and crushed by the pressure. Then the block drops: every foe inside is slammed into the ground and the block shatters in a burst of ice that freezes and chills everything around the crater, leaving a frost field.',
    art: 'Icon: an open hand under a block of ice with silhouettes inside. VFX: foes drawn up into a faceted ice block that frosts over, then the block drops, slams and shatters into glass shards over a frost field.',
    card: { dmg: 'slam', dmgLabel: 'SLAM', area: 'catchR', areaLabel: 'CATCH', count: 'max', countLabel: 'SEIZED', extra: (n, d) => `Crush ${r0(n.crush * d)} × ${n.ticks} · shatter ${r0(n.shatter * d)} in ${n.sr} m · freeze ${n.freeze} s · frost field ${n.fieldT} s` },
    n: { cd: 4.8, reach: 15, catchR: 6.5, max: 16, lift: 0.55, hold: 1.0, h: 4, tick: 20, ticks: 3, crush: 40, slam: 160, sr: 6.5, shatter: 85, freeze: 1.0, chillK: 0.6, chillT: 3, fieldT: 4, fieldK: 0.5, fieldDps: 6, bite: 0.16 } }),
  hail: def({ id: 'hail', a: 'rime', b: 'skyfall', name: 'Hailstorm', col: 0xcdf4ff, col2: 0xa8d8ff,
    fantasy: 'A freezing cone under a rain of ice.',
    desc: 'Breathe a cone of freezing mist that rides with you for two seconds: every foe in it is chilled and takes cold damage. All the while hailstones rain down across the cone, each one bursting on landing and freezing the foes it hits; a hailstone that lands on a foe already frozen shatters it for extra damage around it.',
    art: 'Icon: a mist cone with three hailstones falling into it. VFX: a pale mist cone from the hero with white hailstones dropping through it, small ice bursts where they land and glass shatters on frozen foes.',
    card: { dmg: 'drop', dmgLabel: 'HAILSTONE', area: 'cone', areaLabel: 'CONE', extra: (n, d) => `${n.drops} hailstones / s in ${n.dropR} m · freeze ${n.freeze} s · mist ${r0(n.mist * d)} every ${n.tick} ticks · frozen foes shatter for ${r0(n.shatter * d)} in ${n.sr} m` },
    n: { cd: 4.5, dur: 2.0, cone: 9, half: 0.42, tick: 8, mist: 8, chillK: 0.55, chillT: 2.5, drops: 14, drop: 22, dropR: 1.6, fall: 0.5, freeze: 0.7, shatter: 30, sr: 2.4, bite: 0.03 } }),
};
export const FUSION_IDS = Object.keys(FUSIONS) as FusionId[];
export const isFusionId = (s: string): s is FusionId => Object.prototype.hasOwnProperty.call(FUSIONS, s);
/** the recipe for two techniques, in either order (null = they don't fuse) */
export function recipeFor(a: WeaponId, b: WeaponId): FusionDef | null {
  for (const id of FUSION_IDS) { const f = FUSIONS[id]; if ((f.a === a && f.b === b) || (f.a === b && f.b === a)) return f; }
  return null;
}
/** recipes a technique takes part in */
export const recipesWith = (w: WeaponId): FusionDef[] => FUSION_IDS.map((id) => FUSIONS[id]).filter((f) => f.a === w || f.b === w);

/** Reserved recipes: documented, never offered until they exist (the fire recipes sat here until Fire Breath / Flame Ring
 *  merged). ICE FAMILY (owner 2026-11-03): the family shipped with two live recipes (Glacier Prison, Hailstorm); the other two
 *  ice skills have their recipes reserved for the next fusion pass, so the recipe count stays at 30. */
export const FUSIONS_PENDING: readonly { id: string; a: string; b: string; name: string }[] = [
  { id: 'coldrail', a: 'glacier', b: 'finger', name: 'Coldrail' }, // a piercing needle that leaves a frozen rail: foes crossing it freeze
  { id: 'citadel', a: 'blizzard', b: 'ward', name: 'Frozen Citadel' }, // the dome frosts over: foes touching it freeze; absorbed hits shatter it into ice rings
];

// ---------------------------------------------------------------------------------------------------------------------
// Live state (render reads it, never writes)
// ---------------------------------------------------------------------------------------------------------------------
/** body kinds (render picks a look by kind) */
export const BK = { disc: 1, wheel: 2, wall: 3, decoy: 4, star: 5, shackle: 6, rain: 7, blink: 8, crescent: 9, copy: 10, carom: 11,
  rail: 12, constellation: 13, lattice: 14, skybind: 15, roar: 16, cluster: 17, shatter: 18, flurry: 19, kiln: 20,
  prison: 21, hail: 22 } as const; // ICE FAMILY (owner 2026-11-03): Glacier Prison, Hailstorm
/** shot kinds (orb = a Converging Volley bolt-orb: ph 0 swings out to its ring slot, 1 hovers, 2 dives at the centre) */
export const SK = { bolt: 0, flare: 1, drop: 2, orb: 3 } as const;

export interface FBody {
  alive: boolean; id: number; f: FusionId; kind: number;
  x: number; y: number; z: number; vx: number; vz: number; dx: number; dz: number;
  x0: number; z0: number; r: number; w: number; t: number; life: number; dmg: number; a: number; d: number;
  /** phase / counters (meaning per kind) */ ph: number; k: number; left: number; tid: number;
  hit: Set<number>; ids: number[]; idx: number[]; aux: number[];
}
export interface FShot {
  alive: boolean; id: number; f: FusionId; kind: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; life: number; dmg: number; r: number; br: number; tid: number; dist: number; range: number;
  /** orb (SK.orb): phase, time in it, hover wait, index on the ring; the swing-out curve (x0 → p1 → p2 → slot a) and the ring centre c */
  ph: number; t: number; wait: number; k: number; x0: number; z0: number; p1x: number; p1z: number; p2x: number; p2z: number; ax: number; az: number; cx: number; cz: number;
}
export interface FBeam { on: boolean; f: FusionId; ph: number; t: number; charge: number; dur: number; x: number; z: number; dx: number; dz: number; a0: number; a1: number; len: number; w: number; dmg: number; well: number; wd: number; implode: number; burn: number; tipX: number; tipZ: number }
/** a strip of burning ground. sh = shared burn (sets foes burning at dmg dps for bt s, src/core/burn.ts); else dmg = a hit every 0.25 s.
 *  sear = Magma Front lava: a direct hit every 0.25 s ON TOP of the shared burn (the one fire damage that stacks with a burn) */
export interface FBurn { alive: boolean; f: FusionId; x0: number; z0: number; x1: number; z1: number; t: number; life: number; dmg: number; w: number; sh: boolean; bt: number; sear: number }
/** an orbiting sphere (Sentinel Halo / Tempest Crown) */
export interface FOrb { f: FusionId; k: number; x: number; y: number; z: number; cd: number }
export interface Owned { id: FusionId; /** evolved-partner mask (evoMask) */ evo: number; /** Resonance multiplier */ res: number; t: number; a: number; k: number; cds: number[]; /** Solar Orrery: each sun's last trail point (x, z pairs) */ trail: number[] }

/** fusion-moment and cue events (render: src/render/fusionFx.ts) */
export type FusionEvent =
  /** two partners fused into f at the hero (x, z) */
  | { t: 'fusion'; f: FusionId; a: WeaponId; b: WeaponId; x: number; z: number }
  /** a visual cue of fusion f: k = what happened; (x, z) where, r = size, (dx, dz) a direction when it has one */
  /** Converging Volley: 'volley' = an orb left the hand, 'hover' = the ring is complete (r = ring radius, dx = seconds until
   *  the dive), 'dive' = every orb dives, 'spark' = an orb burst, 'converge' = the core detonation (r = blast radius, dx = foes trapped).
   *  Skybind Press: 'lock' = the rings clamp, 'press' = the knot is driven down.
   *  Roaring Meteor: 'cast' = the leap (r = reach, (dx, dz) toward the landing), 'impact' = the landing (r = crater radius),
   *  'ring' = a roar ring sets out (r = reach, dx = ring index 0..2). */
  /** Magma Front (owner 2026-11-03 rework): 'fissure' = the ground splits (r = wall width, dx/dz = travel), 'erupt' = the
   *  magma wall rises off the crack, 'wall' = it rolls (r = width), 'splash' = it collapses at the end of its run (r = splash
   *  radius), 'lavapop' = a foe died burning and burst into a small lava splash (r = its radius). */
  | { t: 'fuse'; f: FusionId; k: 'cast' | 'impact' | 'implode' | 'freeze' | 'shatter' | 'flurry' | 'pulse' | 'decoy' | 'pop' | 'wall' | 'slam' | 'crush' | 'throw' | 'bounce' | 'lock' | 'press' | 'volley' | 'hover' | 'dive' | 'spark' | 'converge' | 'ring'
    | 'fissure' | 'erupt' | 'splash' | 'lavapop'; x: number; z: number; r: number; dx: number; dz: number };

/** What the fusion behaviours need from the Sim (private helpers are passed as closures, so sim.ts stays encapsulated). */
export interface FusionHost {
  sim: Sim;
  query(x: number, z: number, r: number, cb: (i: number) => void): void;
  setSrc(src: string): void;
  /** mark the following damage as a blast (render hint: KILL_SRC kind, colour, centre, radius); endBlast() after */
  blast(kind: number, col: number, x: number, z: number, r: number): void;
  endBlast(): void;
  crowdSpot(reach: number, r: number, ahead: number): { x: number; z: number } | null;
  countAround(x: number, z: number, r: number): number;
  /** Fire Breath's cone aim (aim mode aware): the direction whose cone (half-angle, reach) covers the most foes; null = none */
  flameAim(range: number, half: number): { dx: number; dz: number } | null;
  /** arena radius (sim.ts ARENA_R): nothing a fusion sends out travels through the wall */
  readonly arenaR: number;
}
/** KILL_SRC values (sim.ts) — duplicated to keep this module free of a runtime import of sim.ts (tests check they match) */
export const KS = { generic: 0, explosion: 1, finger: 2, disc: 3, lance: 4, singularity: 5 } as const;
const DT = 1 / 60, TAU = Math.PI * 2, Y = 1.6; // DT and HOVER_Y (sim.ts)
/** OVERTIME BITE: Resonance deepens the bite by BITE_RES_K per point of Resonance above 1, up to BITE_RES_MAX */
export const BITE_RES_K = 0.15, BITE_RES_MAX = 1.5;
const er = (e: Enemy) => e.def.radius * e.scale;
const isBoss = (e: Enemy) => e.def.kind === 'boss' || e.def.kind === 'herald'; // THE NULL HERALD is never held, pulled, frozen or executed

export class FusionField {
  /** owned fusions in the order they were made (deterministic iteration) */
  readonly owned = new Map<FusionId, Owned>();
  readonly bodies: FBody[] = Array.from({ length: 64 }, () => ({ alive: false, id: 0, f: 'horizon' as FusionId, kind: 0, x: 0, y: 0, z: 0, vx: 0, vz: 0, dx: 0, dz: 1, x0: 0, z0: 0, r: 0, w: 0, t: 0, life: 0, dmg: 0, a: 0, d: 0, ph: 0, k: 0, left: 0, tid: -1, hit: new Set<number>(), ids: [], idx: [], aux: [] }));
  readonly shots: FShot[] = Array.from({ length: 240 }, () => ({ alive: false, id: 0, f: 'horizon' as FusionId, kind: 0, x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, life: 0, dmg: 0, r: 0, br: 0, tid: -1, dist: 0, range: 0, ph: 0, t: 0, wait: 0, k: 0, x0: 0, z0: 0, p1x: 0, p1z: 0, p2x: 0, p2z: 0, ax: 0, az: 0, cx: 0, cz: 0 }));
  readonly beams: FBeam[] = Array.from({ length: 2 }, () => ({ on: false, f: 'horizon' as FusionId, ph: 0, t: 0, charge: 0, dur: 0, x: 0, z: 0, dx: 0, dz: 1, a0: 0, a1: 0, len: 0, w: 0, dmg: 0, well: 0, wd: 0, implode: 0, burn: 0, tipX: 0, tipZ: 0 }));
  readonly burns: FBurn[] = Array.from({ length: 256 }, () => ({ alive: false, f: 'scythelance' as FusionId, x0: 0, z0: 0, x1: 0, z1: 0, t: 0, life: 0, dmg: 0, w: 0, sh: false, bt: 0, sear: 0 }));
  readonly orbs: FOrb[] = [];
  /** Magma Front: queued lava pops (a foe died burning; it bursts after a short delay), oldest first */
  private readonly pops: { x: number; z: number; t: number }[] = [];
  private nextId = 1;
  private readonly tmp = new Set<number>();
  private readonly list: { i: number; d: number; id: number }[] = [];

  constructor(private readonly h: FusionHost) {}

  private get s(): Sim { return this.h.sim; }
  has(id: FusionId): boolean { return this.owned.has(id); }
  /** own a fusion; evo = evolved-partner mask (evoMask) */
  add(id: FusionId, evo = 0): void { if (!this.owned.has(id)) this.owned.set(id, { id, evo, res: resonance(id, evo), t: 0.5, a: 0, k: 0, cds: [], trail: [] }); }
  /** damage multiplier of a fusion: the build's live multiplier × Resonance */
  dmgK(id: FusionId): number { return this.s.dmgMul * (this.owned.get(id)?.res ?? 1); }

  // ------------------------------------------------------------------ helpers
  private ev(f: FusionId, k: Extract<FusionEvent, { t: 'fuse' }>['k'], x: number, z: number, r: number, dx = 0, dz = 0): void { this.s.events.push({ t: 'fuse', f, k, x, z, r, dx, dz }); }
  private body(f: FusionId, kind: number): FBody | null {
    const b = this.bodies.find((q) => !q.alive); if (!b) return null;
    b.alive = true; b.id = this.nextId++; b.f = f; b.kind = kind; b.t = 0; b.ph = 0; b.k = 0; b.left = 0; b.tid = -1; b.a = 0; b.d = 0; b.y = Y; b.vx = b.vz = 0;
    b.hit.clear(); b.ids.length = 0; b.idx.length = 0; b.aux.length = 0; return b;
  }
  private shot(f: FusionId, kind: number): FShot | null {
    const q = this.shots.find((o) => !o.alive); if (!q) return null;
    q.alive = true; q.id = this.nextId++; q.f = f; q.kind = kind; q.tid = -1; q.dist = 0; q.vy = 0; q.ph = 0; q.t = 0; q.wait = 0; q.k = 0; return q;
  }
  /** foes within r (+ body radius) of (x, z), nearest first (id breaks ties); reused list */
  private around(x: number, z: number, r: number, ok?: (e: Enemy) => boolean): { i: number; d: number; id: number }[] {
    const L = this.list; L.length = 0; const s = this.s;
    this.h.query(x, z, r + 3, (i) => { const e = s.enemies[i]; if (!e.alive || (ok && !ok(e))) return; const d = Math.hypot(e.x - x, e.z - z); if (d <= r + er(e)) L.push({ i, d, id: e.id }); });
    L.sort((a, b) => a.d - b.d || a.id - b.id);
    return L;
  }
  private nearest(x: number, z: number, r: number, skip?: Set<number>): number {
    let best = -1, bd = r * r, bid = 0; const s = this.s;
    this.h.query(x, z, r, (i) => { const e = s.enemies[i]; if (!e.alive || skip?.has(e.id)) return; const d = (e.x - x) ** 2 + (e.z - z) ** 2; if (d < bd || (d === bd && e.id < bid)) { bd = d; best = i; bid = e.id; } });
    return best;
  }
  /** foes on the segment from (x0, z0) along unit (dx, dz) for L metres, half-width w */
  private onLine(x0: number, z0: number, dx: number, dz: number, L: number, w: number, cb: (e: Enemy, along: number, side: number) => void): void {
    const s = this.s, cx = x0 + dx * L / 2, cz = z0 + dz * L / 2;
    const hits: { e: Enemy; al: number; sd: number }[] = [];
    this.h.query(cx, cz, L / 2 + w + 3, (i) => {
      const e = s.enemies[i]; if (!e.alive) return;
      const rx = e.x - x0, rz = e.z - z0, al = rx * dx + rz * dz, sd = rx * -dz + rz * dx, rr = er(e);
      if (al < -rr || al > L + rr || Math.abs(sd) > w + rr) return;
      hits.push({ e, al, sd });
    });
    hits.sort((a, b) => a.al - b.al || a.e.id - b.e.id);
    for (const q of hits) cb(q.e, q.al, q.sd);
  }
  /** direction from (x, z) whose line (half-width w, length L) holds the most foes among the directions to the nearest 24
   *  (the nearer foe wins ties); manual aim: the aim. null = nothing in reach */
  private bestDir(x: number, z: number, L: number, w: number): { dx: number; dz: number; n: number } | null {
    const s = this.s, p = s.player;
    if (s.aimMode === 'manual') { const l = Math.hypot(p.aimX, p.aimZ); return l > 1e-6 ? { dx: p.aimX / l, dz: p.aimZ / l, n: 1 } : { dx: p.faceX, dz: p.faceZ, n: 1 }; }
    const c = s.nearestN(x, z, L, 24); if (!c.length) return null;
    let best = -1, bx = 0, bz = 0;
    for (const i of c) {
      const e = s.enemies[i], l = Math.hypot(e.x - x, e.z - z); if (l < 0.3) continue;
      const dx = (e.x - x) / l, dz = (e.z - z) / l; let n = 0;
      for (const j of c) { const o = s.enemies[j], rx = o.x - x, rz = o.z - z, al = rx * dx + rz * dz; if (al >= 0 && al <= L && Math.abs(rx * -dz + rz * dx) <= w + er(o)) n++; }
      if (n > best) { best = n; bx = dx; bz = dz; }
    }
    return best < 0 ? null : { dx: bx, dz: bz, n: best };
  }
  /** densest spot near (x, z): the foe among the nearest 16 within R with the most others within 5 m (null = none) */
  private densest(x: number, z: number, R: number): Enemy | null {
    const s = this.s, c = s.nearestN(x, z, R, 16); let best = -1, bi = -1;
    for (const i of c) { const e = s.enemies[i], n = this.h.countAround(e.x, e.z, 5); if (n > best) { best = n; bi = i; } }
    return bi >= 0 ? s.enemies[bi] : null;
  }
  /** drag foes within R of (x, z) toward it at up to `speed` m/s (heavier foes resist; bosses and held foes don't move) */
  private pull(x: number, z: number, R: number, speed: number, minD = 0.4): void {
    const s = this.s;
    this.h.query(x, z, R + 2, (i) => {
      const e = s.enemies[i]; if (!e.alive || isBoss(e) || e.gripId) return;
      const dx = x - e.x, dz = z - e.z, d = Math.hypot(dx, dz); if (d > R || d < minD) return;
      const step = Math.min(d - minD, speed * DT * (0.45 + 0.55 * (1 - d / R)) / Math.pow(e.def.mass, 0.3));
      e.x += dx / d * step; e.z += dz / d * step;
    });
  }
  /** freeze / stun: bosses 20%, elites and guardians 50% of the time */
  private freeze(e: Enemy, t: number): void { if (!e.alive) return; const k = isBoss(e) || e === this.s.boss ? 0.2 : e.elite ? 0.5 : 1; this.s.freeze(e, t * k); } // (Sim.freeze: Deep Chill hook)
  /** own chain lightning: from (x, z) to the nearest foe within first, then jumps; emits the 'arc' render event */
  private chain(f: FusionId, x: number, y: number, z: number, first: number, jump: number, n: number, dmg: number, col: number): void {
    const s = this.s, hit = this.tmp; hit.clear();
    const pts = [x, y, z]; let cx = x, cz = z, R = first;
    for (let c = 0; c < n; c++) {
      const i = this.nearest(cx, cz, R, hit); if (i < 0) break;
      const e = s.enemies[i]; hit.add(e.id); pts.push(e.x, e.y, e.z);
      this.h.setSrc(f); s.damageEnemy(e, dmg + this.bite(e, f), col, 0, 0); s.chargeHit(e); // (Conductor hook: struck by lightning)
      cx = e.x; cz = e.z; R = jump;
    }
    if (pts.length > 3) s.events.push({ t: 'arc', pts });
  }
  /** set every foe within r of (x, z) burning (shared burn) */
  private burnAround(f: FusionId, x: number, z: number, r: number, dps: number, dur: number): void {
    const s = this.s;
    this.h.query(x, z, r + 3, (i) => { const e = s.enemies[i]; if (e.alive && Math.hypot(e.x - x, e.z - z) <= r + er(e)) s.burn(e, dps + this.bite(e, f, 0.5) / dur, dur, f); });
  }
  /** biteK = share of the fusion's OVERTIME BITE this blast carries (1 = a signature hit, 0.3 = chip) */
  private radial(f: FusionId, x: number, z: number, r: number, dmg: number, col: number, knock: boolean, crater = 0, knockMul = 1, liftMul = 1, kind: number = KS.explosion, biteK = 1): number {
    if (kind === KS.explosion) { const k = this.s.blastMul; if (k !== 1) { this.s.noteBlast(x, z, r, r * k); r *= k; } dmg *= this.s.blastDmgMul; } // Demolition: detonations only
    this.h.setSrc(f); this.h.blast(kind, col, x, z, r);
    const n = this.s.radialDamage(x, Y, z, r, dmg, knock, col, crater, knockMul, liftMul, this.bitePct(f, biteK));
    this.h.endBlast(); return n;
  }
  /** OVERTIME BITE (owner 2026-11-03): the share of a foe's overtime-grown health a hit of fusion f strips (FUSIONS[f].n.bite
   *  × k); evolved partners (Resonance) deepen it a little, up to ×1.5. 0 before overtime and for recipes without a bite. */
  private bitePct(f: FusionId, k = 1): number {
    const b = FUSIONS[f].n.bite; if (!b) return 0;
    const o = this.owned.get(f); return b * k * (o ? Math.min(BITE_RES_MAX, 1 + BITE_RES_K * (o.res - 1)) : 1);
  }
  /** the extra damage of a hit of fusion f on foe e (Sim.lateBite) */
  private bite(e: Enemy, f: FusionId, k = 1): number { const pct = this.bitePct(f, k); return pct > 0 ? this.s.lateBite(e, pct) : 0; }
  private boltAt(f: FusionId, x: number, z: number, e: Enemy, dmg: number): void {
    const q = this.shot(f, SK.bolt); if (!q) return;
    const dx = e.x - x, dz = e.z - z, l = Math.hypot(dx, dz) || 1, sp = 30 * this.s.projSpeedMul * this.s.pace;
    Object.assign(q, { x, y: Y + 0.2, z, vx: dx / l * sp, vz: dz / l * sp, life: 1.3 / this.s.pace, dmg, r: 0.35, tid: e.id });
  }
  private flareAt(f: FusionId, x: number, z: number, dx: number, dz: number, dmg: number, br: number, range: number, speed: number): void {
    const q = this.shot(f, SK.flare); if (!q) return;
    const sp = speed * this.s.projSpeedMul * this.s.pace;
    Object.assign(q, { x, y: Y, z, vx: dx * sp, vz: dz * sp, life: 9, dmg, r: 0.2, br, range });
  }
  /** past the arena wall (minus a margin): travelling fusion pieces stop there */
  private outside(x: number, z: number, m = 0.5): boolean { const R = this.h.arenaR - m; return x * x + z * z > R * R; }
  /** lay a strip of shared-burn ground from (x0, z0) to (x1, z1): foes on it burn at dps for `after` s (refreshed every 0.25 s);
   *  sear = a direct hit every 0.25 s on top of the burn (Magma Front lava) */
  private burnStrip(f: FusionId, x0: number, z0: number, x1: number, z1: number, w: number, dps: number, after: number, life: number, sear = 0): void {
    const q = this.burns.find((b) => !b.alive); if (!q) return;
    q.alive = true; q.f = f; q.x0 = x0; q.z0 = z0; q.x1 = x1; q.z1 = z1; q.t = 0; q.life = life; q.dmg = dps; q.w = w; q.sh = true; q.bt = after; q.sear = sear;
  }
  // ---- Magma Front (owner 2026-11-03 rework) ----
  /** drag the foes of wall b's lane with along ∈ [from, to] and |side| ≤ hw + band sideways onto the lane's centre line at
   *  up to `speed` m/s (heavier foes resist; bosses and held foes don't move). Nothing moves on a held crowd. */
  private laneDrag(b: FBody, band: number, speed: number, from: number, to: number): void {
    const s = this.s, hw = b.w / 2, sx = -b.dz, sz = b.dx, mid = (from + to) / 2;
    this.h.query(b.x0 + b.dx * mid, b.z0 + b.dz * mid, (to - from) / 2 + hw + band + 3, (i) => {
      const e = s.enemies[i]; if (!e.alive || isBoss(e) || e.gripId) return;
      const rx = e.x - b.x0, rz = e.z - b.z0, al = rx * b.dx + rz * b.dz, c = rx * sx + rz * sz, ac = Math.abs(c);
      if (al < from || al > to || ac > hw + band || ac < 0.25) return;
      const k = ac > hw ? 1 : 0.5, step = Math.min(ac - 0.2, speed * k * DT / Math.pow(e.def.mass, 0.3)), sgn = c > 0 ? -1 : 1;
      e.x += sx * sgn * step; e.z += sz * sgn * step;
    });
  }
  /** the wall reached the end of its run: a magma splash (a radial hit that throws foes back), a molten pool through the
   *  centre and a few lava blobs landing around it (seeded), a scorch and a chance to light the grass */
  private magmaSplash(b: FBody, n: Record<string, number>, A: number): void {
    const s = this.s, f: FusionId = 'magma', D = this.dmgK(f), R = n.splashR * A, yaw = Math.atan2(b.dz, b.dx);
    this.radial(f, b.x, b.z, R, n.splash * D * (1 + b.a), 0xff6a1a, true, 0, 1, 1.3, KS.generic); // (a fed wall splashes harder)
    for (let k = 0; k < 3; k++) { // the pool: three molten strips through the splash point
      const a = yaw + k * Math.PI / 3, cx = Math.cos(a) * R * 0.8, cz = Math.sin(a) * R * 0.8;
      this.burnStrip(f, b.x - cx, b.z - cz, b.x + cx, b.z + cz, n.mw * 2.2 * A, n.molten * D, n.after, n.moltenT * s.persistMul, n.sear * D);
    }
    for (let k = 0; k < n.blobs; k++) { // lava blobs land around it
      const a = s.rng.next() * TAU, r = R * (0.9 + s.rng.next() * 0.9), bx = b.x + Math.cos(a) * r, bz = b.z + Math.sin(a) * r;
      if (this.outside(bx, bz, 1)) continue;
      this.burnStrip(f, bx - b.dx * 0.5, bz - b.dz * 0.5, bx + b.dx * 0.5, bz + b.dz * 0.5, 0.9 * A, n.molten * D, n.after, n.moltenT * 0.7 * s.persistMul, n.sear * D);
    }
    s.terrain.addScorch(b.x, b.z, R * 0.6, s.tick);
    if (s.fire.roll(0.5)) s.fire.ignite(b.x, b.z, 1.5, 8, 0); // MAP-FIRE
    this.ev(f, 'splash', b.x, b.z, R, b.dx, b.dz);
  }
  /** a foe died in Magma Front's fire: queue a small lava splash where it stood (bounded queue) */
  private lavaPop(x: number, z: number): void { if (this.pops.length < 24) this.pops.push({ x, z, t: 0.15 }); }
  /** Sim hook: a foe died while burning (Sim.burningKill). Magma Front's burn victims pop into lava splashes. */
  burnKill(e: Enemy): void { if (e.burnSrc === 'magma' && this.owned.has('magma')) this.lavaPop(e.x, e.z); }
  /** fire the lava pops that are due (at most popMax a tick, oldest first): a small radial hit that also sets the foes
   *  around it burning, so a dying crowd chains. Foes never die on a held crowd, so the held-crowd bench sees none of this. */
  private updatePops(): void {
    const P = this.pops; if (!P.length) return;
    if (!this.owned.has('magma')) { P.length = 0; return; }
    const s = this.s, f: FusionId = 'magma', n = FUSIONS.magma.n, D = this.dmgK(f), R = n.popR * s.areaMul;
    let fired = 0, w = 0;
    for (let k = 0; k < P.length; k++) {
      const q = P[k]; q.t -= DT;
      if (q.t > 1e-9 || fired >= n.popMax) { P[w++] = q; continue; }
      fired++;
      this.radial(f, q.x, q.z, R, n.pop * D, 0xffa030, false, 0, 1, 1, KS.generic, 0.5);
      this.burnAround(f, q.x, q.z, R, n.burn * D * 0.6, n.burnT * 0.6 * s.persistMul);
      this.ev(f, 'lavapop', q.x, q.z, R);
    }
    P.length = w;
  }
  private freeHeld(b: FBody): void { // release foes this body holds (grip-style)
    const s = this.s;
    for (let k = 0; k < b.idx.length; k++) { const e = s.enemies[b.idx[k]]; if (e && e.alive && e.id === b.ids[k] && e.gripId === -b.id) { e.lift = Math.max(e.lift, e.gripY); e.gripId = 0; e.gripY = 0; e.ky = 0; } }
  }
  private leap(o: Owned, f: FusionId, air: number, reach: number, r: number): void {
    const s = this.s, p = s.player;
    if (s.dive.active || s.phaseRend.active) { o.t = 0.1; return; }
    const spot = this.h.crowdSpot(reach + r, r, reach * 0.5); if (!spot) { o.t = 0.3; return; }
    const dx = spot.x - p.x, dz = spot.z - p.z, d = Math.hypot(dx, dz), k = d > reach ? reach / d : 1;
    const x1 = p.x + dx * k, z1 = p.z + dz * k;
    const dv = s.dive; dv.active = true; dv.t = 0; dv.dur = air; dv.x0 = p.x; dv.z0 = p.z; dv.x1 = x1; dv.z1 = z1; dv.lvl = 5; dv.evo = true; dv.fx = f;
    p.iframes = Math.max(p.iframes, air + 0.3);
    s.events.push({ t: 'diveStart', x0: p.x, z0: p.z, x1, z1, dur: air, evolved: true });
    if (f === 'roarfall') { const l = Math.hypot(x1 - p.x, z1 - p.z) || 1; this.ev(f, 'cast', p.x, p.z, reach, (x1 - p.x) / l, (z1 - p.z) / l); } // Roaring Meteor: the leap (render / sfx)
    o.t = FUSIONS[f].n.cd * s.cdMul;
  }
  /** a roar ring (Siren Decoy, Roaring Meteor): k = ring index (0 = the roar, 1–2 = echoes), lift = launch speed of the hurl */
  private roarFront(f: FusionId, x: number, z: number, R: number, dmg: number, hurl: number, stun: number, delay: number, k = 0, lift = 4): void {
    const b = this.body(f, BK.roar); if (!b) return;
    Object.assign(b, { x, z, r: R, d: 0, dmg, w: hurl, a: stun, t: -delay, k, vx: lift });
  }

  // ------------------------------------------------------------------ per tick
  update(): void {
    const s = this.s, p = s.player;
    this.orbs.length = 0;
    for (const o of this.owned.values()) {
      const f = o.id, n = FUSIONS[f].n, D = this.dmgK(f), A = s.areaMul;
      this.h.setSrc(f);
      o.t -= DT;
      switch (f) {
        case 'sentinel': case 'tempest': case 'solar': this.ring(o, D); break;
        case 'prismnova': {
          if (s.wardCharges <= 0) { o.k -= DT; if (o.k <= 0) { s.wardCharges = n.charges; s.events.push({ t: 'wardUp', charges: n.charges }); } }
          else if (o.t <= 0) { o.t = n.gap * s.cdMul; this.radial(f, p.x, p.z, n.r * A, n.dmg * D, 0xbfeaff, true, 0, 0.8, 0.3); this.ev(f, 'pulse', p.x, p.z, n.r * A); }
          break;
        }
        default: if (s.training && !s.training.mayFire(f)) o.t = Math.max(o.t, 0.05); /* TRAINING: Manual fire only */ if (o.t <= 0) this.cast(o, D, A);
      }
    }
    this.updateBeams(); this.updateBodies(); this.updateShots(); this.updateBurns(); this.updatePops();
  }

  /** Prism Nova: the dome took a hit (sim.wardAbsorb hook). true = handled. */
  absorb(): boolean {
    const o = this.owned.get('prismnova'); if (!o) return false;
    const s = this.s, p = s.player, n = FUSIONS.prismnova.n, R = n.bigR * s.areaMul;
    s.wardCharges--; p.iframes = 0.45;
    s.events.push({ t: 'wardHit', left: s.wardCharges });
    this.radial('prismnova', p.x, p.z, R, n.big * this.dmgK('prismnova'), 0xffc8f0, true, 0, 1.4, 0.6);
    s.events.push({ t: 'nova', x: p.x, y: p.y, z: p.z, r: R, evolved: true });
    if (s.wardCharges <= 0) { s.wardCharges = 0; o.k = n.recharge * s.cdMul; s.events.push({ t: 'wardBreak', x: p.x, z: p.z, r: R, evolved: true }); }
    return true;
  }

  /** a dive started by a fusion landed at (x, z) (sim.diveLand hook) */
  land(f: FusionId, x: number, z: number, dx: number, dz: number): void {
    const s = this.s, n = FUSIONS[f].n, D = this.dmgK(f), A = s.areaMul, r = n.r * A, cr = n.crater ?? 0.55;
    const hits = this.radial(f, x, z, r, n.land * D, 0xff8a3a, true, cr, n.landKnock ?? 0.9, n.launch / 6, KS.explosion, n.landBite ?? 1); // landBite: Roaring Meteor's big hit bites deeper in overtime
    if (n.landStun) for (const c of this.around(x, z, r)) this.freeze(s.enemies[c.i], n.landStun * s.persistMul); // Roaring Meteor: dazed in the crater
    if (n.landSafe) s.player.iframes = Math.max(s.player.iframes, n.landSafe); // the eruption covers the hero for a moment
    s.events.push({ t: 'diveLand', x, z, r, cr: r * cr, launch: n.launch, hits, evolved: true, fuse: f });
    this.ev(f, 'impact', x, z, r, dx, dz);
    if (f === 'fault') {
      const a0 = Math.atan2(dz, dx) + Math.PI / 4;
      for (let k = 0; k < n.walls; k++) {
        const a = a0 + k * TAU / n.walls, ux = Math.cos(a), uz = Math.sin(a), b = this.body(f, BK.wall); if (!b) break;
        Object.assign(b, { x0: x + ux * 1.2, z0: z + uz * 1.2, x: x + ux * 1.2, z: z + uz * 1.2, dx: ux, dz: uz, d: 0, r: n.wlen, w: n.ww * A, dmg: n.wall * D, life: n.wlen / (n.wspeed * s.pace) });
        this.ev(f, 'wall', b.x, b.z, b.w, ux, uz);
      }
    } else { // roarfall: the roar and two echoes (each a little softer), every one hurling, flipping and stunning
      const R = n.roarR * A;
      for (let k = 0; k < 3; k++) this.roarFront(f, x, z, R, n.roar * D * (k ? n.echo : 1), n.hurl * (k ? n.echoHurl ?? 0.6 : 1), n.stun * s.persistMul, k * n.echoGap, k, (n.lift ?? 4) * (k ? n.echoLiftK ?? 1 : 1));
    }
  }

  /** Sentinel Halo / Tempest Crown: always-on orbiting spheres */
  private ring(o: Owned, D: number): void {
    const s = this.s, p = s.player, f = o.id, n = FUSIONS[f].n, A = s.areaMul, N = n.orbs, R = n.radius * A;
    o.a += n.spin * DT;
    while (o.cds.length < N) o.cds.push(0.2 + o.cds.length * 0.08);
    const base = this.orbs.length;
    for (let k = 0; k < N; k++) {
      const a = o.a + k / N * TAU;
      this.orbs.push({ f, k, x: p.x + Math.cos(a) * R, y: p.y + Math.sin(a * 2) * 0.25, z: p.z + Math.sin(a) * R, cd: o.cds[k] });
    }
    if (f === 'sentinel') {
      for (let k = 0; k < N; k++) {
        const ob = this.orbs[base + k], orR = n.orbR * A;
        this.h.query(ob.x, ob.z, orR + 2, (i) => {
          const e = s.enemies[i]; if (!e.alive || e.orbCd > s.time) return;
          const rr = orR + er(e); if ((e.x - ob.x) ** 2 + (e.z - ob.z) ** 2 >= rr * rr) return;
          e.orbCd = s.time + n.rehit; const dx = e.x - p.x, dz = e.z - p.z, l = Math.hypot(dx, dz) || 1;
          this.h.setSrc(f); s.damageEnemy(e, n.touch * D + this.bite(e, f, 0.5), 0xffd23a, dx / l * 5, dz / l * 5);
        });
        o.cds[k] -= DT; ob.cd = o.cds[k];
        if (o.cds[k] > 0) continue;
        const t = this.nearest(ob.x, ob.z, n.reach);
        if (t < 0) { o.cds[k] = 0.1; continue; }
        o.cds[k] = n.gap * s.cdMul; this.boltAt(f, ob.x, ob.z, s.enemies[t], n.bolt * D);
      }
    } else if (f === 'solar') { // Solar Orrery: burning contact, a trail of burning ground, a flare from every sun now and then
      const orR = n.orbR * A, flare = o.t <= 0;
      if (flare) o.t = n.flare * s.cdMul;
      while (o.trail.length < N * 2) { const ob = this.orbs[base + o.trail.length / 2]; o.trail.push(ob.x, ob.z); }
      for (let k = 0; k < N; k++) {
        const ob = this.orbs[base + k];
        this.h.query(ob.x, ob.z, orR + 2, (i) => {
          const e = s.enemies[i]; if (!e.alive || e.orbCd > s.time) return;
          const rr = orR + er(e); if ((e.x - ob.x) ** 2 + (e.z - ob.z) ** 2 >= rr * rr) return;
          e.orbCd = s.time + n.rehit; const dx = e.x - p.x, dz = e.z - p.z, l = Math.hypot(dx, dz) || 1;
          this.h.setSrc(f); s.damageEnemy(e, n.touch * D + this.bite(e, f), 0xffa030, dx / l * 4, dz / l * 4);
          s.burn(e, n.burn * D + this.bite(e, f, 0.5) / n.burnT, n.burnT, f);
        });
        if ((s.tick + k) % n.trailGap === 0) { // the sun's path since the last drop becomes a strip of burning ground
          if ((ob.x - o.trail[k * 2]) ** 2 + (ob.z - o.trail[k * 2 + 1]) ** 2 < 25) // (not across a leap / teleport)
            this.burnStrip(f, o.trail[k * 2], o.trail[k * 2 + 1], ob.x, ob.z, n.tw * A, n.trail * D, n.after, n.trailT * s.persistMul);
          o.trail[k * 2] = ob.x; o.trail[k * 2 + 1] = ob.z;
        }
        if (flare) {
          const R = n.flareR * A;
          this.radial(f, ob.x, ob.z, R, n.flareDmg * D, 0xff7024, true, 0, 0.5, 0.2);
          this.burnAround(f, ob.x, ob.z, R, n.burn * D, n.burnT);
          s.events.push({ t: 'flameRing', id: 900000 + this.nextId++, x: ob.x, z: ob.z, r: R, speed: R * 4 * s.pace, k: 1, rings: 1, evolved: false });
        }
      }
      if (flare) this.ev(f, 'pulse', p.x, p.z, n.radius * A + n.flareR * A);
    } else { // tempest: lightning links between neighbouring spheres
      if (s.tick % n.linkGap === 0) {
        const hit = this.tmp; hit.clear();
        for (let k = 0; k < N; k++) {
          const a = this.orbs[base + k], b = this.orbs[base + (k + 1) % N], dx = b.x - a.x, dz = b.z - a.z, L = Math.hypot(dx, dz) || 1;
          this.onLine(a.x, a.z, dx / L, dz / L, L, n.linkW, (e) => { if (hit.has(e.id)) return; hit.add(e.id); this.h.setSrc(f); s.damageEnemy(e, n.link * D + this.bite(e, f, 0.3), 0x9fd8ff, 0, 0); });
        }
      }
      for (let k = 0; k < N; k++) {
        o.cds[k] -= DT; const ob = this.orbs[base + k]; ob.cd = o.cds[k];
        if (o.cds[k] > 0) continue;
        o.cds[k] = n.lash * s.cdMul;
        this.chain(f, ob.x, ob.y, ob.z, n.first * A, n.jump * A, n.jumps, n.zap * D, 0x9fd8ff);
      }
    }
  }

  /** start a cast of fusion o (its cooldown has run out) */
  private cast(o: Owned, D: number, A: number): void {
    const s = this.s, p = s.player, f = o.id, n = FUSIONS[f].n, cdK = s.cdMul, V = s.projSpeedMul * s.pace;
    switch (f) {
      case 'horizon': case 'scythelance': {
        if (this.beams.some((b) => b.on && b.f === f)) { o.t = 0.2; return; }
        const w = n.w * A, dir = this.bestDir(p.x, p.z, n.len, w); if (!dir) { o.t = 0.3; return; }
        const bm = this.beams.find((b) => !b.on); if (!bm) { o.t = 0.2; return; }
        o.t = n.cd * cdK;
        const a = Math.atan2(dir.dz, dir.dx), half = f === 'scythelance' ? n.arc * Math.PI / 360 : 0;
        Object.assign(bm, { on: true, f, ph: 0, t: 0, charge: n.charge, dur: n.dur * s.persistMul, x: p.x, z: p.z, dx: Math.cos(a - half), dz: Math.sin(a - half), a0: a - half, a1: a + half, len: n.len, w, dmg: n.dmg * D, well: (n.well ?? 0) * D, wd: 0, implode: (n.implode ?? 0) * D, burn: (n.burn ?? 0) * D, tipX: p.x, tipZ: p.z });
        this.ev(f, 'cast', p.x, p.z, w, bm.dx, bm.dz);
        return;
      }
      case 'reaper': case 'eclipse': {
        const t = this.nearest(p.x, p.z, 20); if (t < 0) { o.t = 0.2; return; }
        o.t = n.cd * cdK;
        const e = s.enemies[t], l = Math.hypot(e.x - p.x, e.z - p.z) || 1, dx = (e.x - p.x) / l, dz = (e.z - p.z) / l;
        for (let k = 0; k < n.count; k++) {
          const a = f === 'reaper' ? (k / n.count) * TAU : (k - (n.count - 1) / 2) * 0.5, c = Math.cos(a), sn = Math.sin(a);
          const b = this.body(f, f === 'reaper' ? BK.disc : BK.wheel); if (!b) break;
          const ux = dx * c - dz * sn, uz = dx * sn + dz * c, sp = n.speed * V, L = n.range;
          Object.assign(b, { x0: p.x, z0: p.z, x: p.x, z: p.z, dx: ux, dz: uz, d: L, w: L * 0.5 * (k % 2 ? -1 : 1), r: n.r * A, dmg: n.dmg * D, life: L * 2.6 / sp, a: 0 });
        }
        this.ev(f, 'throw', p.x, p.z, n.range, dx, dz);
        return;
      }
      case 'shatter': {
        const R = n.r * A; if (s.nearestEnemy(p.x, p.z, R) < 0) { o.t = 0.25; return; }
        o.t = n.cd * cdK;
        const b = this.body(f, BK.shatter); if (!b) return;
        Object.assign(b, { x: p.x, z: p.z, r: R, life: n.delay, dmg: n.shatter * D });
        for (const c of this.around(p.x, p.z, R)) { const e = s.enemies[c.i]; b.ids.push(e.id); b.idx.push(c.i); }
        this.radial(f, p.x, p.z, R, n.dmg * D, 0xe8f8ff, false, 0, 1, 1, KS.generic, 0.3);
        for (let k = 0; k < b.idx.length; k++) { const e = s.enemies[b.idx[k]]; if (e.alive && e.id === b.ids[k]) this.freeze(e, n.freeze); }
        s.events.push({ t: 'glare', x: p.x, z: p.z, r: R, evolved: true });
        this.ev(f, 'freeze', p.x, p.z, R);
        return;
      }
      case 'fault': case 'roarfall': this.leap(o, f, n.air, n.reach, n.r * A); return;
      case 'siren': {
        const spot = this.h.crowdSpot(14, 4, 6); if (!spot) { o.t = 0.3; return; }
        o.t = n.cd * cdK;
        const b = this.body(f, BK.decoy); if (!b) return;
        Object.assign(b, { x: spot.x, z: spot.z, life: n.life * s.persistMul, r: n.pullR * A, dmg: n.aura * D });
        s.events.push({ t: 'lure', x: spot.x, z: spot.z });
        this.ev(f, 'decoy', spot.x, spot.z, b.r);
        return;
      }
      case 'crushstar': case 'skybind': {
        const spot = this.h.crowdSpot(n.reach, 5, 7); if (!spot) { o.t = 0.3; return; }
        const b = this.body(f, f === 'crushstar' ? BK.star : BK.skybind); if (!b) { o.t = 0.3; return; }
        o.t = n.cd * cdK;
        const cap = f === 'skybind' ? Math.max(n.max, Math.round(n.max * A)) : n.max; // Skybind: area upgrades bind more
        Object.assign(b, { x: spot.x, z: spot.z, x0: spot.x, z0: spot.z, r: n.catchR * A, life: 9 });
        for (const c of this.around(spot.x, spot.z, n.catchR * A, (e) => !e.gripId)) {
          if (b.idx.length >= cap) break;
          const e = s.enemies[c.i];
          if (isBoss(e) || e === s.boss) { e.pinT = Math.max(e.pinT, 0.6); continue; }
          e.gripId = -b.id; e.gripY = 0; b.idx.push(c.i); b.ids.push(e.id);
          b.aux.push(e.x - spot.x, e.z - spot.z); // start offsets
        }
        this.ev(f, 'cast', spot.x, spot.z, b.r);
        return;
      }
      case 'shackle': {
        const spot = this.h.crowdSpot(n.reach, 4, 7); if (!spot) { o.t = 0.3; return; }
        const b = this.body(f, BK.shackle); if (!b) { o.t = 0.3; return; }
        o.t = n.cd * cdK;
        const hold = n.hold * s.persistMul;
        const got = this.around(spot.x, spot.z, n.catchR * A).slice(0, n.max).map((c) => ({ ...c, a: Math.atan2(s.enemies[c.i].z - spot.z, s.enemies[c.i].x - spot.x) }));
        got.sort((u, v) => u.a - v.a || u.id - v.id); // a loop around the centre
        for (const c of got) { const e = s.enemies[c.i]; e.pinT = Math.max(e.pinT, hold * (isBoss(e) ? 0.25 : e.elite ? 0.5 : 1)); b.idx.push(c.i); b.ids.push(e.id); }
        Object.assign(b, { x: spot.x, z: spot.z, r: n.catchR * A, life: hold, dmg: n.zap * D });
        this.ev(f, 'cast', spot.x, spot.z, b.r);
        return;
      }
      case 'monsoon': {
        const spot = this.h.crowdSpot(n.reach, 4, 9); if (!spot) { o.t = 0.3; return; }
        o.t = n.cd * cdK;
        const b = this.body(f, BK.rain); if (!b) return;
        let dx = spot.x - p.x, dz = spot.z - p.z; const l = Math.hypot(dx, dz); if (l > 0.1) { dx /= l; dz /= l; } else { dx = p.faceX; dz = p.faceZ; }
        Object.assign(b, { x: spot.x - dx * 2.5, z: spot.z - dz * 2.5, dx, dz, life: n.dur * s.persistMul, r: n.spread * A, dmg: n.dmg * D, w: n.br * A });
        this.ev(f, 'cast', p.x, p.z, b.r, dx, dz);
        return;
      }
      case 'moonstep': case 'stormhands': {
        if (s.nearestEnemy(p.x, p.z, f === 'moonstep' ? n.first : n.reach) < 0) { o.t = 0.3; return; }
        o.t = n.cd * cdK;
        const b = this.body(f, f === 'moonstep' ? BK.blink : BK.flurry); if (!b) return;
        Object.assign(b, { x: p.x, z: p.z, left: f === 'moonstep' ? n.blinks : n.stops, life: 9, k: 0, ph: 0, dx: p.faceX, dz: p.faceZ });
        s.events.push({ t: 'phaseStart', x: p.x, z: p.z, evolved: true });
        return;
      }
      case 'legion': case 'phantom': {
        if (s.nearestEnemy(p.x, p.z, 16) < 0) { o.t = 0.3; return; }
        o.t = n.cd * cdK;
        const t = this.nearest(p.x, p.z, 16), e = s.enemies[t], a0 = Math.atan2(e.z - p.z, e.x - p.x);
        for (let k = 0; k < n.copies; k++) {
          const b = this.body(f, BK.copy); if (!b) break;
          const a = f === 'legion' ? k / n.copies * TAU : a0 + (k - (n.copies - 1) / 2) * 1.2, R = f === 'legion' ? n.ring : n.dist;
          Object.assign(b, { k, a, x: p.x + Math.cos(a) * R, z: p.z + Math.sin(a) * R, life: n.dur * s.persistMul, d: R, left: k * 2, dmg: (f === 'legion' ? n.dmg : n.bolt) * D });
        }
        s.events.push({ t: 'split', n: n.copies, fresh: n.copies, evolved: true });
        return;
      }
      case 'carom': case 'railcomet': {
        const t = this.nearest(p.x, p.z, n.reach); if (t < 0) { o.t = 0.2; return; }
        o.t = n.cd * cdK;
        const e = s.enemies[t], l = Math.hypot(e.x - p.x, e.z - p.z) || 1, dx = (e.x - p.x) / l, dz = (e.z - p.z) / l, sp = n.speed * V;
        const b = this.body(f, f === 'carom' ? BK.carom : BK.rail); if (!b) return;
        Object.assign(b, { x: p.x + dx * 0.8, z: p.z + dz * 0.8, vx: dx * sp, vz: dz * sp, dx, dz, r: n.r, tid: e.id, left: f === 'carom' ? n.bounces : n.hits, life: n.life / s.pace, dmg: n.dmg ?? n.hit, k: 0 });
        this.ev(f, 'throw', p.x, p.z, n.r, dx, dz);
        return;
      }
      case 'cluster': { // Converging Volley: the ring goes around the thickest crowd (led onto where it is heading); the shell lobs onto its centre
        const spot = this.h.crowdSpot(n.reach, 5, 9); if (!spot) { o.t = 0.3; return; }
        const b = this.body(f, BK.cluster); if (!b) { o.t = 0.3; return; }
        o.t = n.cd * cdK;
        // the moments are whole ticks (every orb dives on the same tick; the shell lands exactly as they converge)
        const R = n.ring * A, N = n.orbs + s.echo, kDive = (N - 1) * n.gap + Math.round((n.fly + n.hover) * 60), kLand = kDive + Math.round(n.dive * 60), tDive = kDive * DT, tLand = kLand * DT;
        let vx = 0, vz = 0, k = 0;
        this.h.query(spot.x, spot.z, R + 1, (i) => { const e = s.enemies[i]; if (e.alive && Math.hypot(e.x - spot.x, e.z - spot.z) <= R) { vx += e.vx; vz += e.vz; k++; } });
        let cx = spot.x + (k ? vx / k : 0) * n.lead * tDive, cz = spot.z + (k ? vz / k : 0) * n.lead * tDive;
        { const d = Math.hypot(cx, cz), cap = this.h.arenaR - 1.5; if (d > cap) { cx *= cap / d; cz *= cap / d; } }
        const dx0 = cx - p.x, dz0 = cz - p.z, l0 = Math.hypot(dx0, dz0) || 1, dx = dx0 / l0, dz = dz0 / l0;
        Object.assign(b, { x0: p.x + dx * 0.8, z0: p.z + dz * 0.8, x: p.x + dx * 0.8, z: p.z + dz * 0.8, dx, dz, r: R, w: n.br * A, a: Math.atan2(dz0, dx0), d: tLand, life: tDive, left: N, k: 0, dmg: n.dmg * D, ph: 0 });
        b.aux.push(cx, cz, N, s.tick, s.tick + kDive, s.tick + kLand); // centre, orb count, cast / dive / landing ticks
        this.ev(f, 'cast', cx, cz, R); // the landing telegraph
        s.events.push({ t: 'mortar', x: p.x, y: p.y, z: p.z, dx, dz, size: 0.6 }); // the hero's throw (pose + launch thump)
        return;
      }
      case 'constellation': case 'lattice': {
        const spot = this.h.crowdSpot(n.reach, 5, f === 'constellation' ? 0 : 8); if (!spot) { o.t = 0.3; return; }
        o.t = n.cd * cdK;
        const b = this.body(f, f === 'constellation' ? BK.constellation : BK.lattice); if (!b) return;
        Object.assign(b, { x: spot.x, z: spot.z, r: n.ring * A, a: s.rng.next() * TAU, life: 9, ph: 0, y: Y });
        this.ev(f, 'cast', spot.x, spot.z, b.r);
        return;
      }
      case 'kiln': {
        if (this.bodies.some((b) => b.alive && b.f === f)) { o.t = 0.2; return; }
        const R = n.cone * A, aim = this.h.flameAim(R, n.half); if (!aim) { o.t = 0.3; return; }
        const b = this.body(f, BK.kiln); if (!b) { o.t = 0.3; return; }
        o.t = n.cd * cdK;
        Object.assign(b, { x: p.x, z: p.z, x0: p.x, z0: p.z, dx: aim.dx, dz: aim.dz, r: R, a: n.half, w: n.bw * A, d: n.len * A, life: n.dur * s.persistMul, dmg: n.beam * D });
        this.ev(f, 'cast', p.x, p.z, R * 0.4, aim.dx, aim.dz);
        return;
      }
      case 'magma': { // the fissure opens `ahead` m out along the best lane (ph 0); the wall erupts and rolls in updateBodies
        const W = n.ww * A, dir = this.bestDir(p.x, p.z, n.wlen, W / 2); if (!dir) { o.t = 0.3; return; }
        const b = this.body(f, BK.wall); if (!b) { o.t = 0.3; return; }
        o.t = n.cd * cdK;
        const x0 = p.x + dir.dx * n.ahead, z0 = p.z + dir.dz * n.ahead;
        Object.assign(b, { x0, z0, x: x0, z: z0, dx: dir.dx, dz: dir.dz, d: 0, k: 0, ph: 0, r: n.wlen, w: W, dmg: n.wall * D, life: n.wlen / (n.wspeed * s.pace) });
        s.terrain.addScorch(x0, z0, 1.6, s.tick);
        s.fire.ignite(x0 - dir.dz * (s.rng.next() - 0.5) * W * 0.6, z0 + dir.dx * (s.rng.next() - 0.5) * W * 0.6, 1.2, 7, 0); // MAP-FIRE: the crack lights the grass
        this.ev(f, 'cast', p.x, p.z, W / 2, dir.dx, dir.dz);
        this.ev(f, 'fissure', x0, z0, W, dir.dx, dir.dz);
        return;
      }
      case 'frostpin': {
        const dir = this.bestDir(p.x, p.z, n.range, n.w * A); if (!dir) { o.t = 0.2; return; }
        o.t = n.cd * cdK;
        for (let k = 0; k < n.needles; k++) {
          const a = (k - (n.needles - 1) / 2) * n.fan, c = Math.cos(a), sn = Math.sin(a), ux = dir.dx * c - dir.dz * sn, uz = dir.dx * sn + dir.dz * c;
          const hits: number[] = [];
          this.h.setSrc(f); this.h.blast(KS.finger, -1, p.x, p.z, 0);
          this.onLine(p.x, p.z, ux, uz, n.range, n.w * A, (e) => {
            const frozen = e.stunT > 0; hits.push(e.x, e.y, e.z);
            s.damageEnemy(e, n.dmg * D + this.bite(e, f), 0xff1a3c, ux * 2, uz * 2);
            if (frozen) { this.radial(f, e.x, e.z, n.sr * A, n.shatter * D, 0xe8f8ff, false, 0, 1, 1, KS.explosion); this.ev(f, 'shatter', e.x, e.z, n.sr * A); this.h.setSrc(f); this.h.blast(KS.finger, -1, p.x, p.z, 0); }
            this.freeze(e, n.freeze);
          });
          this.h.endBlast();
          s.events.push({ t: 'finger', x: p.x + ux * 0.6, y: p.y + 0.3, z: p.z + uz * 0.6, ex: p.x + ux * n.range, ey: p.y + 0.3, ez: p.z + uz * n.range, len: n.range, hits, shot: 0, fan: k, evolved: true });
        }
        return;
      }
      // ---- ICE FAMILY recipes (owner 2026-11-03) ----
      case 'prison': { // Glacier Prison: seize the crowd (grip-style hold: gripId = -body id), lift it, encase it in ice, drop it
        const spot = this.h.crowdSpot(n.reach, 5, 7); if (!spot) { o.t = 0.3; return; }
        const b = this.body(f, BK.prison); if (!b) { o.t = 0.3; return; }
        o.t = n.cd * cdK;
        Object.assign(b, { x: spot.x, z: spot.z, x0: spot.x, z0: spot.z, r: n.catchR * A, life: 9 });
        for (const c of this.around(spot.x, spot.z, n.catchR * A, (e) => !e.gripId)) {
          if (b.idx.length >= n.max) break;
          const e = s.enemies[c.i];
          if (isBoss(e) || e === s.boss) { e.pinT = Math.max(e.pinT, 0.6); continue; }
          e.gripId = -b.id; e.gripY = 0; b.idx.push(c.i); b.ids.push(e.id);
          b.aux.push(e.x - spot.x, e.z - spot.z); // start offsets
        }
        s.events.push({ t: 'grip', id: 900000 + b.id, x: spot.x, z: spot.z, r: b.r, n: b.idx.length, h: n.h, evolved: true }); // the hand (Gravity Grip's own cue)
        this.ev(f, 'cast', spot.x, spot.z, b.r);
        return;
      }
      case 'hail': { // Hailstorm: the mist cone rides with the hero while hailstones fall across it
        if (this.bodies.some((q) => q.alive && q.f === f)) { o.t = 0.2; return; }
        const R = n.cone * A, aim = this.h.flameAim(R, n.half); if (!aim) { o.t = 0.3; return; }
        const b = this.body(f, BK.hail); if (!b) { o.t = 0.3; return; }
        o.t = n.cd * cdK;
        Object.assign(b, { x: p.x, z: p.z, x0: p.x, z0: p.z, dx: aim.dx, dz: aim.dz, r: R, a: n.half, life: n.dur * s.persistMul, dmg: n.drop * D, d: 0 });
        s.events.push({ t: 'rime', id: 900000 + b.id, x: p.x, z: p.z, dx: aim.dx, dz: aim.dz, range: R, half: n.half, sweep: 0, dur: b.life, evolved: true, fused: true });
        this.ev(f, 'cast', p.x, p.z, R * 0.4, aim.dx, aim.dz);
        return;
      }
    }
  }

  // ------------------------------------------------------------------ beams (Event Horizon, Scythe Lance)
  private updateBeams(): void {
    const s = this.s, p = s.player;
    for (const bm of this.beams) {
      if (!bm.on) continue;
      const f = bm.f, n = FUSIONS[f].n;
      if (!this.owned.has(f)) { bm.on = false; continue; }
      bm.x = p.x; bm.z = p.z;
      if (bm.ph === 0) { bm.t += DT; if (bm.t >= bm.charge) { bm.ph = 1; bm.t = 0; this.ev(f, 'impact', p.x, p.z, bm.w, bm.dx, bm.dz); } continue; }
      bm.t += DT; const u = Math.min(1, bm.t / bm.dur);
      this.h.setSrc(f);
      if (f === 'scythelance') {
        const e2 = u * u * (3 - 2 * u), a = bm.a0 + (bm.a1 - bm.a0) * e2;
        bm.dx = Math.cos(a); bm.dz = Math.sin(a);
        const R = bm.len * 0.78, tx = p.x + bm.dx * R, tz = p.z + bm.dz * R;
        if (s.tick % 4 === 0) {
          const q = this.burns.find((b) => !b.alive);
          if (q && bm.t > DT * 2) Object.assign(q, { alive: true, f, x0: bm.tipX, z0: bm.tipZ, x1: tx, z1: tz, t: 0, life: n.burnT * s.persistMul, dmg: bm.burn, w: n.burnW * s.areaMul, sh: false, bt: 0 });
          s.terrain.addScorch(tx, tz, 1.1, s.tick);
          bm.tipX = tx; bm.tipZ = tz;
        }
        if (bm.t <= DT * 2) { bm.tipX = tx; bm.tipZ = tz; }
        if (s.tick % n.gap === 0) {
          this.h.blast(KS.lance, 0xfff2c0, p.x, p.z, 0);
          this.onLine(p.x, p.z, bm.dx, bm.dz, bm.len, bm.w, (e) => s.damageEnemy(e, bm.dmg + this.bite(e, f), 0xfff2c0, bm.dx * 4, bm.dz * 4));
          this.h.endBlast();
          if (s.tick % (n.gap * 3) === 0) s.terrain.damageLine(p.x, p.z, bm.dx, bm.dz, bm.len, bm.w, bm.dmg * 2, s.events);
        }
      } else { // horizon
        bm.wd = n.wellFrom + (bm.len - n.wellFrom) * u; // the well rides from wellFrom m out to the tip
        const wx = p.x + bm.dx * bm.wd, wz = p.z + bm.dz * bm.wd, A = s.areaMul;
        // the well drags foes sideways onto the beam line (and a little toward itself)
        const PR = n.pullR * A;
        this.h.query(wx, wz, PR + 2, (i) => {
          const e = s.enemies[i]; if (!e.alive || isBoss(e) || e.gripId) return;
          const rx = e.x - wx, rz = e.z - wz, d = Math.hypot(rx, rz); if (d > PR) return;
          const side = rx * -bm.dz + rz * bm.dx, al = rx * bm.dx + rz * bm.dz, k = n.pull * DT * (0.5 + 0.5 * (1 - d / PR)) / Math.pow(e.def.mass, 0.3);
          const ms = Math.min(Math.abs(side), k), sg = side > 0 ? 1 : -1;
          e.x -= -bm.dz * ms * sg; e.z -= bm.dx * ms * sg;
          const ma = Math.min(Math.abs(al), k * 0.35), sa = al > 0 ? 1 : -1; e.x -= bm.dx * ma * sa; e.z -= bm.dz * ma * sa;
        });
        if (s.tick % 6 === 0) {
          this.h.blast(KS.lance, 0x8fd8ff, p.x, p.z, 0);
          this.onLine(p.x, p.z, bm.dx, bm.dz, bm.len, bm.w, (e) => s.damageEnemy(e, bm.dmg + this.bite(e, f, 0.3), 0xffe28a, 0, 0));
          this.h.endBlast();
          if (s.tick % 18 === 0) s.terrain.damageLine(p.x, p.z, bm.dx, bm.dz, bm.len, bm.w, bm.dmg * 2, s.events);
          for (let d = 3; d < bm.wd; d += 3) s.terrain.addScorch(p.x + bm.dx * d, p.z + bm.dz * d, bm.w, s.tick);
        }
        if (s.tick % 15 === 0) {
          this.h.blast(KS.singularity, 0xff2a3a, wx, wz, n.wellR * A);
          for (const c of this.around(wx, wz, n.wellR * A)) { const e = s.enemies[c.i]; s.damageEnemy(e, bm.well + this.bite(e, f), 0xff2a3a, 0, 0); }
          this.h.endBlast();
        }
        if (u >= 1) {
          const tx = p.x + bm.dx * bm.len, tz = p.z + bm.dz * bm.len, IR = n.implodeR * A;
          this.radial(f, tx, tz, IR, bm.implode, 0xff1450, false, 0.5, 1, 1, KS.singularity);
          s.events.push({ t: 'singCollapse', id: 0, x: tx, z: tz, r: IR, evolved: true, last: true });
          this.ev(f, 'implode', tx, tz, IR);
        }
      }
      if (u >= 1) bm.on = false;
    }
  }

  // ------------------------------------------------------------------ bodies
  private updateBodies(): void {
    const s = this.s, p = s.player;
    for (const b of this.bodies) {
      if (!b.alive) continue;
      const f = b.f, n = FUSIONS[f].n, A = s.areaMul;
      this.h.setSrc(f);
      b.t += DT;
      switch (b.kind) {
        case BK.disc: case BK.wheel: {
          const halo = b.kind === BK.wheel && b.ph === 1;
          if (!halo) {
            const u = Math.min(1, b.t / b.life), fw = Math.sin(Math.PI * u) * b.d, sd = Math.sin(TAU * u) * 0.5 * b.w;
            const ox = b.x0 + (p.x - b.x0) * u * u, oz = b.z0 + (p.z - b.z0) * u * u;
            const nx = ox + b.dx * fw - b.dz * sd, nz = oz + b.dz * fw + b.dx * sd;
            b.vx = (nx - b.x) / DT; b.vz = (nz - b.z) / DT; b.x = nx; b.z = nz;
            if (b.k === 0 && u >= 0.5) { b.k = 1; b.hit.clear(); }
            this.cut(b, f);
            if (b.kind === BK.disc && (s.tick + b.id) % n.zapGap === 0) this.chain(f, b.x, Y, b.z, n.first * A, n.jump * A, n.jumps, n.zap * this.dmgK(f), 0x9fd8ff);
            if (u >= 1) {
              if (b.kind === BK.disc) { b.alive = false; break; }
              b.ph = 1; b.t = 0; b.hit.clear(); b.a = Math.atan2(b.z - p.z, b.x - p.x); b.life = n.haloT * s.persistMul;
            }
          } else {
            b.a += n.haloSpin * DT; const R = n.haloR * A;
            b.x = p.x + Math.cos(b.a) * R; b.z = p.z + Math.sin(b.a) * R;
            if ((s.tick + b.id) % Math.round(n.rehit * 60) === 0) b.hit.clear();
            this.cut(b, f);
            if (b.t >= b.life) b.alive = false;
          }
          break;
        }
        case BK.shatter: {
          if (b.t < b.life) break;
          b.alive = false;
          let frozen = 0; for (let k = 0; k < b.idx.length; k++) { const e = s.enemies[b.idx[k]]; if (e.alive && e.id === b.ids[k] && e.stunT > 0) frozen++; }
          const dmg = b.dmg * (1 + n.per * Math.min(n.perMax, frozen)), R = b.r * 1.1;
          this.radial(f, b.x, b.z, R, dmg, 0xbff4ff, true, 0, 1.1, 0.5);
          s.events.push({ t: 'nova', x: b.x, y: Y, z: b.z, r: R, evolved: true });
          this.ev(f, 'shatter', b.x, b.z, R);
          break;
        }
        case BK.wall: { // Fault Drop walls and the Magma Front
          const magma = f === 'magma';
          if (magma && b.ph === 0) { // Magma Front: the fissure is open (n.fissure s); the lane's foes are dragged onto the line
            this.laneDrag(b, n.pullW * A, n.pull, -1, b.r);
            if (b.t >= n.fissure) { b.ph = 1; b.t = 0; this.ev(f, 'erupt', b.x, b.z, b.w, b.dx, b.dz); this.ev(f, 'wall', b.x, b.z, b.w, b.dx, b.dz); }
            break;
          }
          const d0 = b.d; b.d = Math.min(b.r, b.d + (b.r / b.life) * DT);
          b.x = b.x0 + b.dx * b.d; b.z = b.z0 + b.dz * b.d;
          const hw = b.w / 2, sx = -b.dz, sz = b.dx;
          this.h.blast(KS.explosion, magma ? 0xff6a1a : 0xff80ff, b.x, b.z, hw);
          this.h.query(b.x, b.z, hw + 3, (i) => {
            const e = s.enemies[i]; if (!e.alive || b.hit.has(e.id)) return;
            const rx = e.x - b.x0, rz = e.z - b.z0, al = rx * b.dx + rz * b.dz, c = rx * sx + rz * sz, rr = er(e);
            if (Math.abs(c) > hw + rr || al > b.d + rr || al < d0 - 1.2 - rr) return;
            b.hit.add(e.id); const lit = e.burnT > 0 && e.burnSrc === 'magma'; s.damageEnemy(e, b.dmg * (magma ? 1 + b.a : 1) + this.bite(e, f), magma ? 0xff6a1a : 0xff80ff, 0, 0);
            if (!e.alive) { if (magma) { if (!lit) this.lavaPop(e.x, e.z); b.a = Math.min(n.feedMax, b.a + n.feed); } return; } // Magma Front feeds on what it kills (b.a = the bonus); a victim already burning with this fire pops through burnKill
            s.impulse(e, b.dx * n.push, b.dz * n.push, magma ? n.loft : 3); // Magma Front: flung up and back
            if (magma) s.burn(e, n.burn * this.dmgK(f) + this.bite(e, f, 0.5) / n.burnT, n.burnT, f);
          });
          this.h.endBlast();
          if ((s.tick + b.id) % 6 === 0) { s.terrain.damageLine(b.x - sx * hw, b.z - sz * hw, sx, sz, b.w, 0.8, b.dmg, s.events); s.terrain.addScorch(b.x, b.z, 1.4, s.tick); }
          if (magma) { // Magma Front: an undertow drags foes just ahead of the wall onto its path; the ground behind it is lava
            this.laneDrag(b, n.pullW * A, n.pull * 0.6, b.d, Math.min(b.r, b.d + 4));
            while (b.d - b.k >= n.drop) {
              b.k += n.drop; const cx = b.x0 + b.dx * b.k, cz = b.z0 + b.dz * b.k;
              this.burnStrip(f, cx - sx * hw, cz - sz * hw, cx + sx * hw, cz + sz * hw, n.mw * A, n.molten * this.dmgK(f), n.after, n.moltenT * s.persistMul, n.sear * this.dmgK(f));
            }
            if (b.left === 0 && b.d >= b.r * 0.5) { b.left = 1; if (s.fire.roll(0.35)) s.fire.ignite(b.x, b.z, 1.2, 7, 0); } // MAP-FIRE: the rolling wall lights the grass half way (one roll)
          }
          if (b.d >= b.r || this.outside(b.x, b.z)) { b.alive = false; if (magma) this.magmaSplash(b, n, A); } // the wall breaks on the arena wall (the Magma Front collapses in its splash there too)
          break;
        }
        case BK.roar: {
          if (b.t < 0) break;
          if (b.ph === 0) { b.ph = 1; s.events.push({ t: 'shout', id: 0, x: b.x, z: b.z, dx: 1, dz: 0, half: Math.PI, r: b.r, speed: 30 * s.pace, echo: b.dmg < (n.roar ?? 0) * this.dmgK(f) * 0.99, evolved: true, src: f }); this.ev(f, 'ring', b.x, b.z, b.r, b.k); }
          b.d = Math.min(b.r, b.d + 30 * s.pace * DT);
          this.h.blast(KS.explosion, 0xd2ecff, b.x, b.z, b.r);
          this.h.query(b.x, b.z, b.d + 3, (i) => {
            const e = s.enemies[i]; if (!e.alive || b.hit.has(e.id)) return;
            const rx = e.x - b.x, rz = e.z - b.z, d = Math.hypot(rx, rz); if (d - er(e) > b.d) return;
            b.hit.add(e.id); s.damageEnemy(e, b.dmg + this.bite(e, f), 0xd2ecff, 0, 0); if (!e.alive) return;
            const nx = d > 0.01 ? rx / d : 1, nz = d > 0.01 ? rz / d : 0, k = e.elite ? 0.5 : 1;
            s.impulse(e, nx * b.w * k, nz * b.w * k, e.elite ? 0 : b.vx); this.freeze(e, b.a); // vx = the ring's launch speed (flips foes)
          });
          this.h.endBlast();
          if (b.d >= b.r) b.alive = false;
          break;
        }
        case BK.decoy: {
          this.pull(b.x, b.z, b.r, n.pull, 1.2);
          if ((s.tick + b.id) % 10 === 0) for (const c of this.around(b.x, b.z, n.auraR * A)) { const e = s.enemies[c.i]; s.damageEnemy(e, b.dmg + this.bite(e, f, 0.3), 0xd8c0ff, 0, 0); }
          if (b.t >= b.life) {
            b.alive = false; const D = this.dmgK(f);
            this.roarFront(f, b.x, b.z, n.roarR * A, n.roar * D, n.hurl, n.stun * s.persistMul, 0);
            this.radial(f, b.x, b.z, n.burstR * A, n.burst * D, 0xd8c0ff, false);
            s.events.push({ t: 'explode', x: b.x, y: Y, z: b.z, r: n.burstR * A, color: 0xd8c0ff, lure: true });
            this.ev(f, 'pop', b.x, b.z, n.roarR * A);
          }
          break;
        }
        case BK.star: this.updateHeld(b, f, n, A); break;
        case BK.skybind: this.updateSkybind(b, f, n, A); break;
        case BK.shackle: {
          const pts: number[] = []; let alive = 0;
          for (let k = 0; k < b.idx.length; k++) { const e = s.enemies[b.idx[k]]; if (e.alive && e.id === b.ids[k]) { alive++; if (s.tick % n.gap === 0) pts.push(e.x, e.y + 0.9, e.z); } }
          if (s.tick % n.gap === 0 && pts.length >= 6) {
            if (pts.length >= 9) pts.push(pts[0], pts[1], pts[2]); // close the loop
            const hit = this.tmp; hit.clear();
            for (let k = 0; k + 5 < pts.length; k += 3) {
              const x0 = pts[k], z0 = pts[k + 2], dx = pts[k + 3] - x0, dz = pts[k + 5] - z0, L = Math.hypot(dx, dz); if (L < 0.05) continue;
              this.onLine(x0, z0, dx / L, dz / L, L, n.wireW * A, (e) => { if (!hit.has(e.id)) { hit.add(e.id); s.damageEnemy(e, b.dmg + this.bite(e, f, 0.5), 0xc08aff, 0, 0); } });
            }
            s.events.push({ t: 'arc', pts });
          } else if (s.tick % n.gap === 0 && alive === 1) { const e = b.idx.map((i) => s.enemies[i]).find((q, k) => q.alive && q.id === b.ids[k]); if (e) s.damageEnemy(e, b.dmg + this.bite(e, f, 0.5), 0xc08aff, 0, 0); }
          if (b.t >= b.life || alive === 0) {
            b.alive = false; let pops = 0;
            for (let k = 0; k < b.idx.length; k++) {
              const e = s.enemies[b.idx[k]]; if (!e.alive || e.id !== b.ids[k]) continue;
              e.pinT = 0; const x = e.x, z = e.z;
              this.radial(f, x, z, n.burstR * A, n.burst * this.dmgK(f), 0xd8b8ff, true, 0, 0.6, 0.3);
              if (pops++ < 6) s.events.push({ t: 'explode', x, y: Y, z, r: n.burstR * A, color: 0xd8b8ff });
            }
          }
          break;
        }
        case BK.rain: {
          if (!this.outside(b.x + b.dx * n.walk * DT, b.z + b.dz * n.walk * DT, 2)) { b.x += b.dx * n.walk * DT; b.z += b.dz * n.walk * DT; } // stops at the arena wall
          if ((s.tick + b.id) % n.every === 0) {
            const a = s.rng.next() * TAU, rr = Math.sqrt(s.rng.next()) * b.r, q = this.shot(f, SK.drop);
            if (q) { const T = n.fall / s.pace; Object.assign(q, { x: b.x + Math.cos(a) * rr, z: b.z + Math.sin(a) * rr, y: 11, vx: 0, vz: 0, vy: -(11 - Y) / T, life: T, dmg: b.dmg, br: b.w, r: 0.2 }); }
          }
          if (b.t >= b.life) b.alive = false;
          break;
        }
        case BK.blink: case BK.flurry: this.updateBlinker(b, f, n, A); break;
        case BK.crescent: {
          const d0 = b.d; b.d = Math.min(b.r, b.d + b.w * DT); // w = speed here
          b.x = b.x0 + b.dx * b.d; b.z = b.z0 + b.dz * b.d;
          const hw = b.a / 2, sx = -b.dz, sz = b.dx; // a = width
          this.h.blast(KS.disc, -1, b.x, b.z, 0);
          this.h.query(b.x, b.z, hw + 3, (i) => {
            const e = s.enemies[i]; if (!e.alive || b.hit.has(e.id)) return;
            const rx = e.x - b.x0, rz = e.z - b.z0, al = rx * b.dx + rz * b.dz, c = rx * sx + rz * sz, rr = er(e);
            if (Math.abs(c) > hw + rr) return;
            const q = Math.min(1, Math.abs(c) / hw), lead = 0.28 * b.a * (1 - q * q);
            if (al > b.d + lead + rr || al < d0 + lead - n.thick - rr || al < -0.5) return;
            b.hit.add(e.id); s.damageEnemy(e, b.dmg + this.bite(e, f), 0xb8a6ff, b.dx * 3, b.dz * 3);
          });
          this.h.endBlast();
          if (b.d >= b.r || this.outside(b.x, b.z)) b.alive = false;
          break;
        }
        case BK.copy: this.updateCopy(b, f, n, A); break;
        case BK.kiln: this.updateKiln(b, f, n); break;
        case BK.prison: this.updatePrison(b, f, n, A); break; // ICE FAMILY (owner 2026-11-03)
        case BK.hail: this.updateHail(b, f, n, A); break;
        case BK.carom: case BK.rail: this.updateRicochet(b, f, n, A); break;
        case BK.cluster: this.updateVolley(b, f, n, A); break;
        case BK.constellation: case BK.lattice: this.updateRing(b, f, n, A); break;
      }
    }
  }

  /** Kiln Sight beam k's direction at this moment (render reads it too): the two beams scissor through the cone */
  static kilnDir(b: FBody, passes: number, k: number): { dx: number; dz: number } {
    const u = Math.min(1, b.t / Math.max(1e-6, b.life)), a = Math.atan2(b.dz, b.dx) + (k ? -1 : 1) * b.a * Math.cos(Math.PI * passes * u);
    return { dx: Math.cos(a), dz: Math.sin(a) };
  }
  /** Kiln Sight: the flame cone rides with the hero (every foe in it is hit and set burning) while the two eye beams sweep
   *  through it; every strip of ground a beam crosses becomes burning ground */
  private updateKiln(b: FBody, f: FusionId, n: Record<string, number>): void {
    const s = this.s, p = s.player, D = this.dmgK(f);
    b.x = p.x; b.z = p.z;
    if ((s.tick + b.id) % n.tick === 0) {
      this.h.query(b.x, b.z, b.r + 3, (i) => {
        const e = s.enemies[i]; if (!e.alive || !inCone(b.x, b.z, b.dx, b.dz, b.a, b.r, e.x, e.z, er(e))) return;
        s.damageEnemy(e, n.flame * D + this.bite(e, f, 0.3), 0xff8c2a, 0, 0);
        s.burn(e, n.burn * D + this.bite(e, f, 0.5) / n.burnT, n.burnT, f);
      });
    }
    for (let k = 0; k < 2; k++) {
      const { dx, dz } = FusionField.kilnDir(b, n.passes, k);
      if ((s.tick + b.id) % n.gap === 0) {
        this.h.blast(KS.lance, 0xffd080, b.x, b.z, 0);
        this.onLine(b.x, b.z, dx, dz, b.d, b.w, (e) => s.damageEnemy(e, b.dmg + this.bite(e, f), 0xfff0b0, dx * 2, dz * 2));
        this.h.endBlast();
      }
      if ((s.tick + b.id + k * 2) % n.drop === 0) {
        this.burnStrip(f, b.x + dx * 1.2, b.z + dz * 1.2, b.x + dx * b.d * 0.95, b.z + dz * b.d * 0.95, n.fw * s.areaMul, n.field * D, n.after, n.fieldT * s.persistMul);
        s.terrain.addScorch(b.x + dx * b.d * 0.6, b.z + dz * b.d * 0.6, 1.1, s.tick);
      }
    }
    if (b.t >= b.life) b.alive = false;
  }

  // ------------------------------------------------------------------ ICE FAMILY recipes (owner 2026-11-03)
  /** Glacier Prison: ph 0 = the lift (foes drawn into the block and raised to h), then the encasement (every held foe frozen,
   *  crushed every `tick` ticks); at lift + hold the block drops: every held foe is slammed into the ground, the block
   *  shatters (shatter in sr, knock), everything around is frozen and chilled, and a frost field is left. Cues: 'cast'
   *  (the hand), 'freeze' (the block closes), 'slam' (dx = foes slammed), plus Gravity Grip's 'gripSlam' and 'frostburst'. */
  private updatePrison(b: FBody, f: FusionId, n: Record<string, number>, A: number): void {
    const s = this.s, D = this.dmgK(f), tLift = n.lift, tHold = tLift + n.hold * s.persistMul;
    const held = (k: number): Enemy | null => { const e = s.enemies[b.idx[k]]; return e.alive && e.id === b.ids[k] && e.gripId === -b.id ? e : null; };
    const u = Math.min(1, b.t / tLift), e2 = u * u * (3 - 2 * u);
    for (let k = 0; k < b.idx.length; k++) {
      const e = held(k); if (!e) continue;
      const ox = b.aux[k * 2], oz = b.aux[k * 2 + 1], sq = 1 - 0.6 * e2;
      e.x = b.x0 + ox * sq; e.z = b.z0 + oz * sq; e.gripY = n.h * e2;
      e.vx = e.vz = e.kx = e.kz = 0;
      if (b.k) e.stunT = Math.max(e.stunT, 0.05); // encased: frozen solid for as long as the block holds (the +25% frozen bonus applies)
    }
    if (b.t >= tLift && !b.k) { // the block closes
      b.k = 1;
      for (let k = 0; k < b.idx.length; k++) { const e = held(k); if (e) { s.iceFreeze(e, (n.hold + 0.2) * s.persistMul); s.chill(e, n.chillK, n.chillT); } }
      this.ev(f, 'freeze', b.x0, b.z0, b.r, 0, n.h);
    }
    if (b.k && (s.tick + b.id) % n.tick === 0) { // the pressure crushes the foes inside
      this.h.blast(KS.explosion, 0xbdf0ff, b.x0, b.z0, b.r);
      for (let k = 0; k < b.idx.length; k++) { const e = held(k); if (e) s.damageEnemy(e, n.crush * D + this.bite(e, f, 0.3), 0xbdf0ff, 0, 0); }
      this.h.endBlast();
    }
    if (b.t < tHold) return;
    // the drop: the block slams into the ground and shatters
    b.alive = false;
    let slammed = 0;
    for (let k = 0; k < b.idx.length; k++) {
      const e = held(k); if (!e) continue;
      e.gripId = 0; e.gripY = 0; e.lift = 0; e.ky = 0; slammed++;
      this.h.blast(KS.explosion, 0xbdf0ff, b.x0, b.z0, b.r);
      s.damageEnemy(e, n.slam * D + this.bite(e, f), 0xbdf0ff, 0, 0);
      this.h.endBlast();
      if (e.alive) { s.chill(e, n.chillK, n.chillT); s.iceFreeze(e, n.freeze * s.persistMul); }
    }
    this.freeHeld(b);
    const R = n.sr * A;
    this.radial(f, b.x0, b.z0, R, n.shatter * D, 0xe4fbff, true, 0.4, 0.8, 0.4, KS.explosion, 0.5);
    for (const c of this.around(b.x0, b.z0, R)) { const e = s.enemies[c.i]; s.chill(e, n.chillK, n.chillT); s.iceFreeze(e, n.freeze * s.persistMul * 0.6); }
    s.frostField(b.x0, b.z0, R * 0.8, n.fieldT * s.persistMul, n.fieldK, n.fieldDps * D, f, true);
    s.events.push({ t: 'gripSlam', id: 900000 + b.id, x: b.x0, z: b.z0, r: b.r, shockR: R, n: slammed, evolved: true }, { t: 'frostburst', x: b.x0, z: b.z0, r: R });
    this.ev(f, 'slam', b.x0, b.z0, R, slammed, 0);
  }
  /** Hailstorm: the mist cone rides with the hero (every `tick` ticks every foe in it takes mist damage and a chill) while
   *  hailstones (SK.drop shots) fall on foes in the cone `drops` times a second; they land in updateShots (freeze, shatter). */
  private updateHail(b: FBody, f: FusionId, n: Record<string, number>, A: number): void {
    const s = this.s, p = s.player, D = this.dmgK(f);
    b.x = p.x; b.z = p.z;
    const inCone_ = (e: Enemy) => inCone(b.x, b.z, b.dx, b.dz, b.a, b.r, e.x, e.z, er(e));
    if ((s.tick + b.id) % n.tick === 0) {
      this.h.query(b.x, b.z, b.r + 3, (i) => {
        const e = s.enemies[i]; if (!e.alive || !inCone_(e)) return;
        s.damageEnemy(e, n.mist * D + this.bite(e, f, 0.3), 0xcdf4ff, 0, 0);
        if (e.alive) s.chill(e, n.chillK, n.chillT);
      });
    }
    b.d += DT * n.drops;
    while (b.d >= 1) { // a hailstone: aimed at a foe in the cone (the seeded rng picks which), else at a point in the cone
      b.d -= 1;
      const c = this.around(b.x, b.z, b.r, inCone_);
      let tx: number, tz: number;
      if (c.length) { const e = s.enemies[c[Math.floor(s.rng.next() * c.length)].i]; tx = e.x + (s.rng.next() - 0.5) * 0.8; tz = e.z + (s.rng.next() - 0.5) * 0.8; }
      else { const d = b.r * (0.3 + 0.7 * s.rng.next()), a = (s.rng.next() - 0.5) * 2 * b.a; tx = b.x + (b.dx * Math.cos(a) - b.dz * Math.sin(a)) * d; tz = b.z + (b.dx * Math.sin(a) + b.dz * Math.cos(a)) * d; }
      const q = this.shot(f, SK.drop); if (!q) break;
      Object.assign(q, { x: tx, y: Y + 7, z: tz, vx: 0, vz: 0, vy: -7 / n.fall, life: n.fall, dmg: b.dmg, r: 0.2, br: n.dropR * A, range: 0 });
    }
    if (b.t >= b.life) b.alive = false;
  }
  /** a hailstone lands: a small ice burst (batters foes back a little), every foe in it is frozen and chilled; a foe that was
   *  already frozen shatters for `shatter` damage around it (one shatter per hailstone, the nearest frozen foe) */
  private hailLand(q: FShot): void {
    const s = this.s, f = q.f, n = FUSIONS[f].n, D = this.dmgK(f), A = s.areaMul;
    let frozen: Enemy | null = null;
    for (const c of this.around(q.x, q.z, q.br)) { const e = s.enemies[c.i]; if (e.stunT > 0) { frozen = e; break; } }
    const hits = this.radial(f, q.x, q.z, q.br, q.dmg, 0xe4fbff, true, 0, 0.35, 0.15, KS.generic, 1);
    for (const c of this.around(q.x, q.z, q.br)) { const e = s.enemies[c.i]; s.chill(e, n.chillK, n.chillT); s.iceFreeze(e, n.freeze * s.persistMul); }
    this.ev(f, 'spark', q.x, q.z, q.br, hits, 0);
    if (frozen && frozen.alive) {
      const R = n.sr * A;
      this.radial(f, frozen.x, frozen.z, R, n.shatter * D, 0xe8f8ff, false, 0, 1, 1, KS.explosion, 0.5);
      s.events.push({ t: 'frostburst', x: frozen.x, z: frozen.z, r: R });
      this.ev(f, 'shatter', frozen.x, frozen.z, R);
    }
  }

  /** disc / wheel contact cut (once per foe per leg / halo window) */
  private cut(b: FBody, f: FusionId): void {
    const s = this.s;
    this.h.blast(KS.disc, -1, b.x, b.z, 0);
    this.h.query(b.x, b.z, b.r + 3, (i) => {
      const e = s.enemies[i]; if (!e.alive || b.hit.has(e.id)) return;
      const rr = b.r + er(e); if ((e.x - b.x) ** 2 + (e.z - b.z) ** 2 >= rr * rr) return;
      b.hit.add(e.id); const v = Math.hypot(b.vx, b.vz) || 1;
      s.damageEnemy(e, b.dmg + this.bite(e, f), f === 'reaper' ? 0xc6b8ff : 0xb8a6ff, b.vx / v * 3, b.vz / v * 3);
      if (b.kind === BK.wheel && b.ph === 1 && e.alive) { const p = s.player, ox = e.x - p.x, oz = e.z - p.z, l = Math.hypot(ox, oz) || 1; s.impulse(e, ox / l * FUSIONS[f].n.haloPush, oz / l * FUSIONS[f].n.haloPush, 2); } // the halo throws foes outward
    });
    this.h.endBlast();
  }

  /** Crushing Star: seized foes are lifted and held (gripId = -body id), crushed into a star, then the star is hurled */
  private updateHeld(b: FBody, f: FusionId, n: Record<string, number>, A: number): void {
    const s = this.s, D = this.dmgK(f);
    const tLift = n.lift, tHold = n.lift + n.hold;
    if (b.ph === 0) {
      const u = Math.min(1, b.t / tLift), e2 = u * u * (3 - 2 * u);
      for (let k = 0; k < b.idx.length; k++) {
        const e = s.enemies[b.idx[k]]; if (!e.alive || e.id !== b.ids[k] || e.gripId !== -b.id) continue;
        const ox = b.aux[k * 2], oz = b.aux[k * 2 + 1];
        const sq = 1 - 0.85 * e2; e.x = b.x0 + ox * sq; e.z = b.z0 + oz * sq; e.gripY = n.h * e2;
        e.vx = e.vz = e.kx = e.kz = 0;
      }
      if ((s.tick + b.id) % 15 === 0) {
        for (let k = 0; k < b.idx.length; k++) { const e = s.enemies[b.idx[k]]; if (e.alive && e.id === b.ids[k] && e.gripId === -b.id) s.damageEnemy(e, n.tick * D + this.bite(e, f, 0.3), 0xffe9a8, 0, 0); }
      }
      if (b.t < tHold) return;
      // crush
      let caught = 0;
      for (let k = 0; k < b.idx.length; k++) {
        const e = s.enemies[b.idx[k]]; if (!e.alive || e.id !== b.ids[k] || e.gripId !== -b.id) continue;
        caught++;
        this.h.blast(KS.explosion, 0xffe9a8, b.x0, b.z0, b.r);
        s.damageEnemy(e, n.crush * D + this.bite(e, f), 0xffe9a8, 0, 0);
        this.h.endBlast();
      }
      this.freeHeld(b);
      // the star is hurled at the densest crowd near the grab
      const tgt = this.densest(b.x0, b.z0, 20);
      b.ph = 1; b.t = 0; b.k = caught; b.x = b.x0; b.z = b.z0; b.y = n.h + Y;
      b.dx = tgt ? tgt.x : b.x0; b.dz = tgt ? tgt.z : b.z0; // target point
      this.ev(f, 'crush', b.x0, b.z0, 1 + caught * 0.08);
      return;
    }
    // ph 1: the star flies
    const u = Math.min(1, b.t / (n.fly / s.pace));
    b.x = b.x0 + (b.dx - b.x0) * u; b.z = b.z0 + (b.dz - b.z0) * u; b.y = Y + n.h * (1 - u * u) + 2 * Math.sin(Math.PI * u);
    if (u < 1) return;
    b.alive = false;
    const R = Math.min(n.brMax, n.br + n.brK * b.k) * A;
    this.radial(f, b.x, b.z, R, (n.blast + n.perFoe * b.k) * D, 0xffe9a8, true, 0.55, 1.2, 0.8);
    s.events.push({ t: 'explode', x: b.x, y: Y, z: b.z, r: R, color: 0xffd060 });
    this.ev(f, 'impact', b.x, b.z, R);
  }

  /** Skybind Press (owner 2026-11-03 rework): ph 0 = the yank up (lift) and the bound knot (every bound foe spins into a tight
   *  knot while the rings tick), ph 1 = the press (the knot is driven straight down), then the slam: every bound foe takes the
   *  slam, is finished off under `exec` of its max HP (elites: half that) and lies dazed in the crater; the ground shock
   *  (shock + perFoe per bound foe) hurls and dazes everything else in shockR. Bound foes never move or attack (sim.restrain).
   *  Render cues: 'cast' (the yank), 'lock' (rings shut), 'press' (the drive down), 'slam' (dx = foes slammed). */
  private updateSkybind(b: FBody, f: FusionId, n: Record<string, number>, A: number): void {
    const s = this.s, D = this.dmgK(f), m = b.idx.length;
    const tLift = n.lift, tHold = tLift + n.bound * s.persistMul;
    const held = (k: number): Enemy | null => { const e = s.enemies[b.idx[k]]; return e.alive && e.id === b.ids[k] && e.gripId === -b.id ? e : null; };
    if (b.ph === 0) {
      const u = Math.min(1, b.t / tLift), up = 1 - (1 - u) * (1 - u) * (1 - u); // a yank: fast, easing out at the top
      const tight = Math.min(1, b.t / tHold), pull = 1 - (1 - tight) * (1 - tight); // the knot tightens over the whole bound phase
      for (let k = 0; k < m; k++) {
        const e = held(k); if (!e) continue;
        const ox = b.aux[k * 2], oz = b.aux[k * 2 + 1], R0 = Math.hypot(ox, oz) || 0.5, a0 = Math.atan2(oz, ox);
        const Rin = n.inner * A * (0.3 + 0.7 * Math.sqrt((k + 0.5) / m)); // a tight knot, the first caught at its core
        const R = R0 + (Rin - R0) * pull, a = a0 + n.spin * b.t * b.t / tHold; // the spin winds up
        e.x = b.x0 + Math.cos(a) * R; e.z = b.z0 + Math.sin(a) * R;
        e.gripY = n.h * up * (0.85 + 0.3 * ((k * 0.618034) % 1)) + (u >= 1 ? Math.sin(b.t * 9 + k) * 0.08 : 0);
        e.vx = e.vz = e.kx = e.kz = 0;
      }
      if ((s.tick + b.id) % n.gap === 0) for (let k = 0; k < m; k++) { const e = held(k); if (e) s.damageEnemy(e, n.tick * D + this.bite(e, f, 0.3), 0xff6ad0, 0, 0); }
      if (b.t >= tLift && b.k === 0) { b.k = 1; this.ev(f, 'lock', b.x0, b.z0, n.h); }
      if (b.t < tHold) return;
      b.ph = 1; b.t = 0;
      for (let k = 0; k < m; k++) { const e = held(k); b.aux[2 * m + k] = e ? e.gripY : 0; b.aux[3 * m + k] = e ? Math.atan2(e.z - b.z0, e.x - b.x0) : 0; b.aux[4 * m + k] = e ? Math.hypot(e.x - b.x0, e.z - b.z0) : 0; }
      this.ev(f, 'press', b.x0, b.z0, n.h);
      return;
    }
    // ph 1: the press: the knot is driven straight down, closing a little more on the way
    const u = Math.min(1, b.t / n.drop), down = u * u;
    for (let k = 0; k < m; k++) {
      const e = held(k); if (!e) continue;
      const R = b.aux[4 * m + k] * (1 - 0.25 * u), a = b.aux[3 * m + k] + n.spin * 0.2 * u;
      e.x = b.x0 + Math.cos(a) * R; e.z = b.z0 + Math.sin(a) * R; e.gripY = b.aux[2 * m + k] * (1 - down);
      e.vx = e.vz = e.kx = e.kz = 0;
    }
    if (u < 1) return;
    // the slam
    const stun = n.stun * s.persistMul; let caught = 0;
    this.h.blast(KS.explosion, 0xff6ad0, b.x0, b.z0, b.r);
    for (let k = 0; k < m; k++) {
      const e = held(k); if (!e) continue;
      caught++; s.freeze(e, stun); // dazed first (plating is down while stunned); Sim.freeze: the Deep Chill hook
      s.damageEnemy(e, n.slam * D + this.bite(e, f), 0xff6ad0, 0, 0);
      if (e.alive && e.hp <= e.maxHp * n.exec * (e.elite ? 0.5 : 1)) s.damageEnemy(e, e.hp + 1, 0xffffff, 0, 0); // finished off
    }
    this.h.endBlast();
    this.freeHeld(b);
    const R = n.shockR * A;
    this.radial(f, b.x0, b.z0, R, (n.shock + n.perFoe * caught) * D, 0xff9ae0, true, 0.5, 0.9, 0.5);
    for (const c of this.around(b.x0, b.z0, R)) this.freeze(s.enemies[c.i], stun); // everything in the shock is dazed (bosses 20 %, elites 50 %)
    for (let k = 0; k < m; k++) { const e = s.enemies[b.idx[k]]; if (e.alive && e.id === b.ids[k]) { e.lift = 0; e.ky = 0; e.kx = 0; e.kz = 0; } } // the slammed lie flat in the crater
    this.ev(f, 'slam', b.x0, b.z0, R, caught, 0);
    b.alive = false;
  }

  /** Moonstep / Storm of Hands: an afterimage body blinks between foes / clusters */
  private updateBlinker(b: FBody, f: FusionId, n: Record<string, number>, A: number): void {
    const s = this.s, p = s.player, D = this.dmgK(f);
    if (f === 'stormhands' && b.k > 0 && b.aux.length) { // strikes still to land at the current stop
      if ((b.aux[0] -= 1) <= 0) {
        b.aux[0] = n.sgap; b.aux[1]--;
        this.radial(f, b.x, b.z, n.r * A, n.dmg * D, 0xff8f8f, true, 0, n.knock, 0, KS.generic);
        for (const c of this.around(b.x, b.z, n.r * A)) this.freeze(s.enemies[c.i], n.stagger); // every strike staggers
        if (b.aux[1] <= 0) b.aux.length = 0;
      }
    }
    if (b.t < 0) return;
    if (b.left <= 0) { s.events.push({ t: 'phaseEnd', fx: b.x, fz: b.z, x: p.x, z: p.z }); b.alive = false; return; }
    const first = b.k === 0, fx = first ? p.x : b.x, fz = first ? p.z : b.z;
    let tx: number, tz: number, hits = 0;
    if (f === 'moonstep') {
      const i = this.nearest(fx, fz, first ? n.first : n.jump, b.hit); if (i < 0) { b.left = 0; return; }
      const e = s.enemies[i]; b.hit.add(e.id); tx = e.x; tz = e.z;
    } else {
      const c = s.nearestN(p.x, p.z, n.reach, 24); let best = -1, bi = -1;
      for (const i of c) {
        const e = s.enemies[i]; let near = false;
        for (let q = 0; q < b.ids.length; q += 2) if ((e.x - b.ids[q] / 100) ** 2 + (e.z - b.ids[q + 1] / 100) ** 2 < 16) near = true;
        if (near) continue;
        const nn = this.h.countAround(e.x, e.z, 3); if (nn > best) { best = nn; bi = i; }
      }
      if (bi < 0) { b.left = 0; return; }
      tx = s.enemies[bi].x; tz = s.enemies[bi].z; b.ids.push(Math.round(tx * 100), Math.round(tz * 100));
    }
    let dx = tx - fx, dz = tz - fz; const l = Math.hypot(dx, dz); if (l > 1e-4) { dx /= l; dz /= l; } else { dx = b.dx; dz = b.dz; }
    const bx = tx + dx * 0.9, bz = tz + dz * 0.9;
    if (f === 'moonstep') {
      const L = n.slashL, x0 = tx - dx * L / 2, z0 = tz - dz * L / 2;
      this.h.blast(KS.disc, -1, tx, tz, 0);
      this.onLine(x0, z0, dx, dz, L, n.slashW, (e) => { hits++; s.damageEnemy(e, n.slash * D + this.bite(e, f), 0xc3a6ff, dx * 4, dz * 4); });
      this.h.endBlast();
      const c = this.body(f, BK.crescent);
      if (c) Object.assign(c, { x0: tx + dx * 0.5, z0: tz + dz * 0.5, x: tx, z: tz, dx, dz, d: 0, r: n.crange, w: n.cspeed * s.projSpeedMul * s.pace, a: n.cw * A, dmg: n.cdmg * D });
      b.t = -n.gap * DT;
    } else {
      b.aux.length = 0; b.aux.push(0, n.strikes); // first strike lands this tick
      this.ev(f, 'flurry', tx, tz, n.r * A);
      b.t = -n.gap * DT;
    }
    s.events.push({ t: 'phaseBlink', k: b.k, fx, fz, x: bx, z: bz, tx, tz, dx, dz, len: f === 'moonstep' ? n.slashL : n.r * A * 2, hits, cut: 0, evolved: true });
    b.x = bx; b.z = bz; b.dx = dx; b.dz = dz; b.k++; b.left--;
    p.iframes = Math.max(p.iframes, 0.1);
  }

  /** Gatling Legion / Phantom Host copies */
  private updateCopy(b: FBody, f: FusionId, n: Record<string, number>, A: number): void {
    const s = this.s, p = s.player;
    if (f === 'legion') {
      b.a += n.spin * DT; b.x = p.x + Math.cos(b.a) * b.d; b.z = p.z + Math.sin(b.a) * b.d;
      if ((b.left -= 1) <= 0) {
        const t = this.nearest(b.x, b.z, n.reach);
        if (t < 0) b.left = 6;
        else {
          b.left = n.gap; const e = s.enemies[t], l = Math.hypot(e.x - b.x, e.z - b.z) || 1, sp = (s.rng.next() * 2 - 1) * 0.1;
          const ux = (e.x - b.x) / l, uz = (e.z - b.z) / l, c = Math.cos(sp), sn = Math.sin(sp);
          b.dx = ux; b.dz = uz;
          this.flareAt(f, b.x + ux * 0.6, b.z + uz * 0.6, ux * c - uz * sn, ux * sn + uz * c, b.dmg, n.br * A, n.range, n.speed);
        }
      }
    } else {
      this.pull(b.x, b.z, n.pullR * A, n.pull, 1.0);
      if ((b.left -= 1) <= 0) {
        const t = this.nearest(b.x, b.z, n.reach);
        b.left = t < 0 ? 6 : Math.round(n.gap * 60);
        if (t >= 0) { const e = s.enemies[t]; b.dx = e.x - b.x; b.dz = e.z - b.z; this.boltAt(f, b.x, b.z, e, b.dmg); }
      }
    }
    if (b.t < b.life) return;
    b.alive = false;
    s.events.push({ t: 'splitEnd', id: 0, x: b.x, z: b.z });
    if (f === 'phantom') {
      const R = n.br * A; this.radial(f, b.x, b.z, R, n.blast * this.dmgK(f), 0xd8c0ff, true, 0.3, 0.9, 0.5);
      s.events.push({ t: 'explode', x: b.x, y: Y, z: b.z, r: R, color: 0xd8c0ff, lure: true });
      this.ev(f, 'pop', b.x, b.z, R);
    }
  }

  /** Converging Volley shell at time t of its flight (render reads it too): a high lob from the throw point to the ring centre */
  static shellAt(b: FBody, t: number, h: number): { x: number; y: number; z: number } {
    const u = Math.max(0, Math.min(1, t / Math.max(1e-6, b.d))), cx = b.aux[0], cz = b.aux[1];
    return { x: b.x0 + (cx - b.x0) * u, y: Y + h * 4 * u * (1 - u), z: b.z0 + (cz - b.z0) * u };
  }
  /** Converging Volley: the body is the heavy shell and the conductor. It launches an orb every `gap` ticks (each swings out to
   *  its slot on the ring, then hovers), lobs itself onto the ring centre so it lands as the orbs converge, drags foes inside
   *  the ring inward during the dive and detonates on landing: core blast + a bonus per foe trapped, a stun, and ordinary
   *  foes left under `exec` of their health are finished (elites under `execElite`, bosses never). */
  private updateVolley(b: FBody, f: FusionId, n: Record<string, number>, A: number): void {
    const s = this.s, D = this.dmgK(f), cx = b.aux[0], cz = b.aux[1], N = b.aux[2], k0 = b.aux[3], kDive = b.aux[4], kLand = b.aux[5]; // (b.t is stepped by updateBodies)
    while (b.left > 0 && s.tick - k0 >= b.k * n.gap) { this.launchOrb(b, f, n, A, D, b.k, N, kDive); b.k++; b.left--; if (b.left === 0) this.ev(f, 'hover', cx, cz, b.r, (kDive - s.tick) * DT, 0); }
    const sh = FusionField.shellAt(b, b.t, n.h); b.x = sh.x; b.y = sh.y; b.z = sh.z;
    if (b.ph === 0 && s.tick >= kDive) { b.ph = 1; this.ev(f, 'dive', cx, cz, b.r); }
    if (b.ph === 1 && s.tick < kLand) this.pull(cx, cz, b.r, n.pull, 0.8);
    if (s.tick < kLand) return;
    b.alive = false;
    const L = this.around(cx, cz, b.r), inside = L.length, bonus = Math.min(n.maxBonus, n.perFoe * inside);
    this.radial(f, cx, cz, b.w, b.dmg * (1 + bonus), 0x46e05a, true, 0.45, 1.1, 0.5);
    s.events.push({ t: 'mortarImpact', x: cx, z: cz, r: b.w, evo: true, color: 0x46e05a });
    for (const q of L) {
      const e = s.enemies[q.i]; if (!e.alive || e.id !== q.id) continue;
      this.freeze(e, n.stun);
      const thr = isBoss(e) || e === s.boss ? 0 : e.elite ? n.execElite : n.exec;
      if (thr > 0 && e.hp < e.maxHp * thr) { this.h.setSrc(f); s.damageEnemy(e, e.hp + 1, 0xffffff, 0, 0); }
    }
    this.ev(f, 'converge', cx, cz, b.w, inside, 0);
  }
  /** orb k of N leaves the throw point on a wide swing (cubic curve) to its slot on the ring; slots fan out both sides from
   *  the far point, so the first orbs look like they fly past the crowd; every control point stays inside the arena */
  private launchOrb(b: FBody, f: FusionId, n: Record<string, number>, A: number, D: number, k: number, N: number, kDive: number): void {
    const q = this.shot(f, SK.orb); if (!q) return;
    const cx = b.aux[0], cz = b.aux[1], R = b.r, j = k === 0 ? 0 : k % 2 ? (k + 1) / 2 : N - k / 2, a = b.a + j / N * TAU;
    const cap = this.h.arenaR - 0.8, clamp = (x: number, z: number): [number, number] => { const d = Math.hypot(x, z); return d > cap ? [x * cap / d, z * cap / d] : [x, z]; };
    const [ax, az] = clamp(cx + Math.cos(a) * R, cz + Math.sin(a) * R);
    const lx = ax - b.x0, lz = az - b.z0, L = Math.hypot(lx, lz) || 1, px = -b.dz, pz = b.dx, side = Math.sign((ax - cx) * px + (az - cz) * pz) || 1;
    const ox = (ax - cx) / (Math.hypot(ax - cx, az - cz) || 1), oz = (az - cz) / (Math.hypot(ax - cx, az - cz) || 1);
    const [p1x, p1z] = clamp(b.x0 + lx * 0.4 + px * side * 0.35 * L, b.z0 + lz * 0.4 + pz * side * 0.35 * L);
    const [p2x, p2z] = clamp(ax + ox * 0.55 * R + px * side * 0.15 * L, az + oz * 0.55 * R + pz * side * 0.15 * L);
    Object.assign(q, { x: b.x0, y: Y + 0.3, z: b.z0, vx: lx / L * 20, vz: lz / L * 20, life: n.fly, dmg: n.orb * D, r: 0.45, br: n.orbR * A, ph: 0, t: 0, wait: kDive, k, x0: b.x0, z0: b.z0, p1x, p1z, p2x, p2z, ax, az, cx, cz }); // wait = the shared dive tick
    this.ev(f, 'volley', b.x0, b.z0, 0.3, lx / L, lz / L);
  }
  /** an orb: swing out (ease-out along its curve), hover until the shared dive moment, then dive straight at the centre
   *  (ease-in, every orb arrives together) and burst on the first foe met, or at the centre */
  private updateOrb(q: FShot, n: Record<string, number>): void {
    const s = this.s;
    q.t += DT;
    if (q.ph === 0) {
      const u0 = Math.min(1, q.t / q.life), u = 1 - Math.pow(1 - u0, 2.2), v = 1 - u;
      const x = v * v * v * q.x0 + 3 * v * v * u * q.p1x + 3 * v * u * u * q.p2x + u * u * u * q.ax, z = v * v * v * q.z0 + 3 * v * v * u * q.p1z + 3 * v * u * u * q.p2z + u * u * u * q.az;
      q.vx = (x - q.x) / DT; q.vz = (z - q.z) / DT; q.x = x; q.z = z; q.y = Y + 0.3 + 0.5 * u;
      if (u0 >= 1 || s.tick >= q.wait) { q.ph = 1; q.t = 0; q.life = Math.max(0, q.wait - s.tick) * DT; q.vx = q.vz = 0; } // life = the hover left (render: primed in the last beat)
      if (s.tick < q.wait) return;
    }
    if (q.ph === 1) {
      if (s.tick < q.wait) return; // the shared dive tick
      q.ph = 2; q.t = 0; q.life = n.dive; q.ax = q.x; q.az = q.z; // the dive starts from where it hovers
    }
    const u = Math.min(1, (s.tick - q.wait) / Math.max(1, Math.round(q.life * 60))), w = u * u, x = q.ax + (q.cx - q.ax) * w, z = q.az + (q.cz - q.az) * w; // arrives on the landing tick
    q.vx = (x - q.x) / DT; q.vz = (z - q.z) / DT; q.x = x; q.z = z; q.y = Y + 0.8 - 0.4 * w;
    let hit: Enemy | null = null, bd = Infinity;
    this.h.query(q.x, q.z, q.r + 3, (i) => {
      const e = s.enemies[i]; if (!e.alive) return;
      const rr = q.r + er(e), d = (e.x - q.x) ** 2 + (e.z - q.z) ** 2; if (d < rr * rr && (d < bd || (d === bd && hit && e.id < hit.id))) { bd = d; hit = e; }
    });
    if (!hit && u < 1) return;
    q.alive = false;
    const sp = Math.hypot(q.vx, q.vz) || 1;
    this.radial(q.f, q.x, q.z, q.br, q.dmg, 0xffd23a, false, 0, 0.4, 0.2);
    this.ev(q.f, 'spark', q.x, q.z, q.br, q.vx / sp, q.vz / sp);
  }

  /** Caroming Sun / Railshot Comet (ricochet between foes) */
  private updateRicochet(b: FBody, f: FusionId, n: Record<string, number>, A: number): void {
    const s = this.s, D = this.dmgK(f);
    if (b.tid >= 0) { // steer hard toward the target
      const t = s.enemyById(b.tid);
      if (t) { const dx = t.x - b.x, dz = t.z - b.z, l = Math.hypot(dx, dz) || 1, sp = Math.hypot(b.vx, b.vz); b.vx += (dx / l * sp - b.vx) * 0.35; b.vz += (dz / l * sp - b.vz) * 0.35; }
      else b.tid = -1;
    }
    b.x += b.vx * DT; b.z += b.vz * DT;
    const v = Math.hypot(b.vx, b.vz) || 1; b.dx = b.vx / v; b.dz = b.vz / v;
    let hit: Enemy | null = null, bd = Infinity;
    this.h.query(b.x, b.z, b.r + 3, (i) => {
      const e = s.enemies[i]; if (!e.alive || b.hit.has(e.id)) return;
      const rr = b.r + er(e), d = (e.x - b.x) ** 2 + (e.z - b.z) ** 2; if (d < rr * rr && (d < bd || (d === bd && hit && e.id < hit.id))) { bd = d; hit = e; }
    });
    const out = b.t >= b.life || this.outside(b.x, b.z); // spent, or at the arena wall
    if (!hit && !out) return;
    if (!hit) { b.alive = false; s.events.push({ t: 'cometEnd', id: 0, x: b.x, z: b.z, split: false, shard: false }); return; }
    const e = hit as Enemy; b.hit.add(e.id);
    if (f === 'carom') {
      const R = n.br * (1 + n.grow * b.k) * A, dmg = n.dmg * (1 + n.dgrow * b.k) * D;
      this.radial(f, b.x, b.z, R, dmg, 0x9aff5a, true, 0.35, 0.7, 0.5, KS.explosion, 1 + n.dgrow * b.k);
      s.events.push({ t: 'mortarImpact', x: b.x, z: b.z, r: R, evo: true, color: 0x9aff5a });
    } else {
      this.h.blast(KS.generic, -1, b.x, b.z, 0); s.damageEnemy(e, n.hit * D + this.bite(e, f), 0x5affd8, b.dx * 4, b.dz * 4); this.h.endBlast();
    }
    b.k++; b.left--;
    const nx = this.nearest(b.x, b.z, n.seek * A, b.hit);
    if (f === 'railcomet' && nx >= 0) { // the needle cracks ahead to the next target, piercing every foe on the line
      const t = s.enemies[nx], dx = t.x - b.x, dz = t.z - b.z, l = Math.hypot(dx, dz) || 1, ux = dx / l, uz = dz / l, hits: number[] = [];
      this.h.blast(KS.finger, -1, b.x, b.z, 0);
      this.onLine(b.x, b.z, ux, uz, n.nlen, n.nw * A, (o) => { hits.push(o.x, o.y, o.z); s.damageEnemy(o, n.needle * D + this.bite(o, f), 0xff1a3c, ux * 2.5, uz * 2.5); });
      this.h.endBlast();
      s.events.push({ t: 'finger', x: b.x, y: Y + 0.3, z: b.z, ex: b.x + ux * n.nlen, ey: Y + 0.3, ez: b.z + uz * n.nlen, len: n.nlen, hits, shot: 0, fan: 0, evolved: false });
    }
    this.ev(f, 'bounce', b.x, b.z, f === 'carom' ? n.br * A : 1, b.dx, b.dz);
    if (b.left <= 0 || nx < 0) { b.alive = false; s.events.push({ t: 'cometEnd', id: 0, x: b.x, z: b.z, split: false, shard: false }); return; }
    const t = s.enemies[nx]; b.tid = t.id;
    const dx = t.x - b.x, dz = t.z - b.z, l = Math.hypot(dx, dz) || 1, sp = Math.hypot(b.vx, b.vz);
    b.vx = dx / l * sp; b.vz = dz / l * sp;
  }

  /** Constellation Heart / Gravity Lattice: a ring that hangs around the crowd, then merges into one strike */
  private updateRing(b: FBody, f: FusionId, n: Record<string, number>, A: number): void {
    const s = this.s, D = this.dmgK(f);
    if (f === 'constellation') {
      const t1 = n.hang * s.persistMul, t2 = t1 + n.rise, t3 = t2 + n.hold, t4 = t3 + n.drop;
      if (b.t < t1) { this.pull(b.x, b.z, b.r, n.pull, 1.5); return; }
      if (b.t < t3) { if (b.ph === 0) { b.ph = 1; this.ev(f, 'cast', b.x, b.z, b.r * 0.4); } return; }
      if (b.t < t4) { b.y = Y + n.h * (1 - (b.t - t3) / n.drop); return; }
      b.alive = false; const R = n.br * A;
      this.radial(f, b.x, b.z, R, n.dmg * D, 0xffe08c, true, 0.6, 1.2, 0.9);
      s.events.push({ t: 'zenithImpact', x: b.x, z: b.z, r: R, comet: true });
      this.ev(f, 'impact', b.x, b.z, R);
      return;
    }
    // lattice
    const life = n.life * s.persistMul;
    b.a += n.spin * DT;
    if (b.t < life) {
      for (let k = 0; k < n.holes; k++) {
        const a = b.a + k / n.holes * TAU, hx = b.x + Math.cos(a) * b.r, hz = b.z + Math.sin(a) * b.r;
        this.pull(hx, hz, n.pullR * A, n.pull, 0.5);
        if ((s.tick + k) % 15 === 0) { this.h.blast(KS.singularity, 0xff2a3a, hx, hz, n.coreR * A); for (const c of this.around(hx, hz, n.coreR * A)) { const e = s.enemies[c.i]; s.damageEnemy(e, n.tick * D + this.bite(e, f, 0.3), 0xff2a3a, 0, 0); } this.h.endBlast(); }
      }
      return;
    }
    if (b.t < life + n.merge) { this.pull(b.x, b.z, b.r + 2, n.pull * 1.5, 0.5); return; }
    b.alive = false; const R = n.ir * A;
    this.radial(f, b.x, b.z, R, n.implode * D, 0xff1450, false, 0.5, 1, 1, KS.singularity);
    s.events.push({ t: 'singCollapse', id: 0, x: b.x, z: b.z, r: R, evolved: true, last: true });
    this.ev(f, 'implode', b.x, b.z, R);
  }

  // ------------------------------------------------------------------ shots
  private updateShots(): void {
    const s = this.s;
    for (const q of this.shots) {
      if (!q.alive) continue;
      this.h.setSrc(q.f);
      if (q.kind === SK.orb) { this.updateOrb(q, FUSIONS[q.f].n); continue; } // (keeps its own clock)
      q.life -= DT;
      if (q.kind === SK.drop) {
        q.y += q.vy * DT;
        if (q.life > 0) continue;
        q.alive = false;
        if (q.f === 'hail') { this.hailLand(q); continue; } // ICE FAMILY: a hailstone (freeze + shatter, its own cue)
        const n = this.radial(q.f, q.x, q.z, q.br, q.dmg, 0xffb02a, true, 0, 0.5, 0.2); // the drops batter foes back
        s.events.push({ t: 'barragePop', id: 0, x: q.x, y: Y, z: q.z, r: q.br, n, hit: n > 0, evolved: true });
        continue;
      }
      if (q.kind === SK.bolt && q.tid >= 0) {
        const t = s.enemyById(q.tid);
        if (t) { const dx = t.x - q.x, dz = t.z - q.z, l = Math.hypot(dx, dz) || 1, sp = Math.hypot(q.vx, q.vz); q.vx += (dx / l * sp - q.vx) * 0.2; q.vz += (dz / l * sp - q.vz) * 0.2; }
        else q.tid = -1;
      }
      const sp = Math.hypot(q.vx, q.vz) || 1;
      let step = sp * DT, out = q.life <= 0 || this.outside(q.x, q.z);
      if (q.kind === SK.flare && q.dist + step >= q.range) { step = q.range - q.dist; out = true; }
      q.x += q.vx / sp * step; q.z += q.vz / sp * step; q.dist += step;
      let hit: Enemy | null = null, bd = Infinity;
      this.h.query(q.x, q.z, q.r + 3, (i) => {
        const e = s.enemies[i]; if (!e.alive) return;
        const rr = q.r + er(e), d = (e.x - q.x) ** 2 + (e.z - q.z) ** 2; if (d < rr * rr && (d < bd || (d === bd && hit && e.id < hit.id))) { bd = d; hit = e; }
      });
      if (!hit && !out) continue;
      q.alive = false;
      if (q.kind === SK.flare) {
        const n = this.radial(q.f, q.x, q.z, q.br, q.dmg, 0xffb02a, false);
        s.events.push({ t: 'barragePop', id: 0, x: q.x, y: Y, z: q.z, r: q.br, n, hit: !!hit, evolved: true });
      } else if (hit) {
        const e = hit as Enemy;
        s.damageEnemy(e, q.dmg + this.bite(e, q.f), 0xffd23a, q.vx / sp * 3, q.vz / sp * 3);
        s.events.push({ t: 'boltHit', x: q.x, y: q.y, z: q.z });
      }
    }
  }

  private updateBurns(): void {
    const s = this.s; let any = false;
    for (const b of this.burns) {
      if (!b.alive) continue;
      b.t += DT; if (b.t >= b.life) { b.alive = false; continue; }
      any = true;
    }
    if (!any || s.tick % 15 !== 0) return;
    const hit = this.tmp; hit.clear();
    for (const b of this.burns) {
      if (!b.alive) continue;
      this.h.setSrc(b.f);
      const dx = b.x1 - b.x0, dz = b.z1 - b.z0, L = Math.hypot(dx, dz); if (L < 0.05) continue;
      if (b.sh) this.onLine(b.x0, b.z0, dx / L, dz / L, L, b.w, (e) => { // shared burn: never stacks; lava sear: one direct hit per foe per tick on top of it
        s.burn(e, b.dmg + this.bite(e, b.f, 0.3) / b.bt, b.bt, b.f);
        if (b.sear > 0 && !hit.has(e.id)) { hit.add(e.id); s.damageEnemy(e, b.sear + this.bite(e, b.f, 0.3), 0xff8a20, 0, 0); } // (a kill here pops through burnKill: the foe burns)
      });
      else this.onLine(b.x0, b.z0, dx / L, dz / L, L, b.w, (e) => { if (!hit.has(e.id)) { hit.add(e.id); s.damageEnemy(e, b.dmg + this.bite(e, b.f, 0.3), 0xff5a1a, 0, 0); } });
    }
  }

  /** TRAINING: un-own a fusion and drop its live effects (held foes are released) */
  remove(id: FusionId): void {
    if (!this.owned.delete(id)) return;
    for (const b of this.bodies) if (b.alive && b.f === id) { this.freeHeld(b); b.alive = false; }
    for (const q of this.shots) if (q.alive && q.f === id) q.alive = false;
    for (const b of this.beams) if (b.on && b.f === id) b.on = false;
    for (const b of this.burns) if (b.alive && b.f === id) b.alive = false;
    if (id === 'magma') this.pops.length = 0;
    const s = this.s; if (s.dive.fx === id) { s.dive.active = false; s.dive.fx = undefined; }
  }

  /** drop all live effects (a fusion run reset) */
  clearLive(): void {
    for (const b of this.bodies) if (b.alive) { this.freeHeld(b); b.alive = false; }
    for (const q of this.shots) q.alive = false;
    for (const b of this.beams) b.on = false;
    for (const b of this.burns) b.alive = false;
    this.orbs.length = 0; this.pops.length = 0;
  }
}

/** areas that are reaches (never scaled by Focus Lens) */
const FIXED_AREA = new Set(['range', 'nlen']);
/** '2.5%' style label of an OVERTIME BITE share (cards, Encyclopedia) */
export const biteLabel = (pct: number): string => `${(pct * 100).toFixed(pct * 100 >= 10 ? 0 : 1).replace(/\.0$/, '')}%`;
/** Encyclopedia sentence for a fusion's OVERTIME BITE ('' when it has none) */
export const fusionBiteText = (id: FusionId): string => { const b = FUSIONS[id].n.bite; return b ? ` Past the 30-minute mark its hits also strip ${biteLabel(b)} of the health a foe has grown since then (elites half, guardians a fifth), so it keeps killing however tough the horde gets.` : ''; };
/** cooldown of a fusion (s, before the live multiplier): its cast cooldown, else its repeat beat */
export const fusionCd = (id: FusionId): number => { const n = FUSIONS[id].n; return n.cd ?? (id === 'tempest' ? n.lash : id === 'solar' ? n.flare : n.gap); };
/** Card stat rows for a fusion (live multipliers applied), shared by the level-up card, pause planner and Encyclopedia. */
export function fusionCardRows(id: FusionId, m: { cdMul: number; dmgMul: number; areaMul: number }, evo = 0): { k: string; label: string; v: string }[] {
  const F = FUSIONS[id], c = F.card, n = F.n, res = resonance(id, evo), d = m.dmgMul * res;
  const fmt = (v: number) => (v >= 100 ? String(Math.round(v)) : v >= 10 ? v.toFixed(0) : v.toFixed(1));
  const rows = [{ k: 'cd', label: id === 'sentinel' || id === 'tempest' || id === 'prismnova' || id === 'solar' ? 'EVERY' : 'COOLDOWN', v: `${fmt(fusionCd(id) * m.cdMul)} s` }, { k: 'dmg', label: c.dmgLabel, v: fmt(n[c.dmg] * d) }];
  if (c.area !== undefined) rows.push({ k: 'area', label: c.areaLabel ?? 'AREA', v: `${fmt(n[c.area] * (FIXED_AREA.has(c.area) ? 1 : m.areaMul))} m` });
  if (c.count !== undefined) rows.push({ k: 'count', label: c.countLabel ?? 'COUNT', v: String(typeof c.count === 'number' ? c.count : n[c.count]) });
  if (c.extra) rows.push({ k: 'extra', label: '', v: c.extra(n, d) });
  const fl = evoFloor(id); // the floor is shown as its own row; RESONANCE only when evolved partners give more than it
  if (res > fl + 0.001) rows.push({ k: 'evo', label: 'RESONANCE', v: `×${res.toFixed(2)}` });
  else if (fl > 1.001) rows.push({ k: 'floor', label: 'EVOLUTION FLOOR', v: `×${fl.toFixed(2)}` });
  if (n.bite) rows.push({ k: 'bite', label: 'OVERTIME', v: `strips ${biteLabel(n.bite * Math.min(BITE_RES_MAX, 1 + BITE_RES_K * (res - 1)))} of grown health per hit` });
  return rows;
}
